import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Menu,
  X,
  Loader2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Link2,
  Send,
  Copy,
  Check,
  ExternalLink,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { apiUrl } from "../utils/apiBase";
import { trackPlatformActivity } from "../api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useWallet } from "@vechain/dapp-kit-react";
import {
  disconnectEverywhere,
  findExistingSession,
} from "../utils/walletconnectClient";

import Dashboard from "../components/Dashboard";
import WalletConnectModal from "../components/WalletConnectModal";
import HowV3TaxWorksModal from "../components/HowV3TaxWorksModal";
import RoadmapModal from "../components/RoadmapModal";
import LegalNoticesModal from "../components/LegalNoticesModal";
import HelpSupportModal from "../components/HelpSupportModal";
import { formatPlanDisplay, normalizePlanId, PlanBadge } from "../components/PlanBadge";
import {
  resolvePlanTxLimit,
  isUnlimitedPlan,
  formatTxLimitDisplay,
} from "../utils/planUtils";
import { APP_VERSION_LABEL, COPYRIGHT_LABEL } from "../appVersion";
import {
  translateBackendJobDetailToGerman,
  translateBackendJobDetailToEnglish,
} from "../utils/analysisJobDetailI18n";

const MOBILE_TELEGRAM_URL = import.meta.env.VITE_TELEGRAM_URL || "";
const MOBILE_X_URL = import.meta.env.VITE_X_URL || "";
const V3TAX_DESKTOP_URL = "https://v3tax.app";

const MOBILE_COINGECKO_LOGO_SRC = "/brand/coingecko-api-lockup.png";
const MOBILE_VECHAINSTATS_LOGO_SRC = "/vs.jpg";
const MOBILE_COINGECKO_URL = "https://www.coingecko.com/en/api";
const MOBILE_VECHAINSTATS_URL = "https://vechainstats.com";
const MOBILE_VECHAIN_EXPLORER_ACCOUNT_URL =
  import.meta.env.VITE_VECHAIN_EXPLORER_ACCOUNT_URL ||
  "https://vechainstats.com/account/";

const MOBILE_GALAXY_FALLBACK_ICON_SRC = "/nft-icons/earth.png";

const MOBILE_EARTH_GALAXY_TIER = {
  key: "earth",
  label: "Earth",
  icon: MOBILE_GALAXY_FALLBACK_ICON_SRC,
  rank: 0,
};

const isRealMobileGalaxyTier = (tier) =>
  Boolean(
    tier?.key &&
      String(tier.key).toLowerCase() !== "earth" &&
      String(tier.icon || "").trim()
  );

const MOBILE_MANUAL_DISCONNECT_KEY = "v3tax:mobile_manual_disconnect";
const MOBILE_EMBEDDED_LOGIN_RELOAD_KEY = "v3tax:embedded_login_reload";

const MOBILE_INSTANT_SCAN_ADMIN_WALLETS = new Set([
  "0x3c1d3cc2a44569cecbef586c1fcd8cbb821b9c5e",
]);

const parseDatasetList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.datasets)) return payload.datasets;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const getDatasetObject = (payload) =>
  payload?.analysisResult?.dataset ??
  payload?.dataset ??
  payload?.processedData ??
  null;

const buildProfitBlock = (totals = {}) => {
  const swapProfit = Number(totals?.disposalNetEUR ?? totals?.disposalEUR ?? 0);
  const otherIncome = Number(totals?.otherIncomeEUR ?? totals?.incomeEUR ?? 0);
  const total =
    Number(totals?.totalTaxableEUR ?? (swapProfit + otherIncome));

  return {
    swapProfit,
    otherIncome,
    total,
    disposalShortTermNetEUR: Number(totals?.disposalShortTermNetEUR || 0),
    disposalLongTermNetEUR: Number(totals?.disposalLongTermNetEUR || 0),
  };
};

const buildLiquidityBlock = (totals = {}) => {
  const swapProfit = Number(
    totals?.disposalNetEUR ??
      totals?.disposalProfitEUR ??
      totals?.netProfitEUR ??
      0
  );
  const otherIncome = Number(totals?.otherIncomeEUR || 0);
  return {
    swapProfit,
    otherIncome,
    total: Number(totals?.totalTaxableEUR ?? swapProfit + otherIncome),
  };
};

const mapSummaryFromPayload = ({ payload, datasetMeta, fallbackWallet }) => {
  const root = payload?.snapshot ?? payload;
  const ds = getDatasetObject(root);
  if (!ds) return null;

  const selectedCountry = String(
    root?.meta?.countryCode ||
      ds?.country ||
      ds?.countryCode ||
      datasetMeta?.country ||
      "DE"
  ).toUpperCase();

  const selectedYear = String(
    ds?.year ||
      datasetMeta?.year ||
      new Date().getFullYear()
  );

  const summaryProps = {
    vet: buildProfitBlock(ds?.vetTotals),
    vtho: buildProfitBlock(ds?.vthoTotals),
    b3tr: buildProfitBlock(ds?.b3trTotals),
    nft: {
      swapProfit: Number(ds?.nftTotals?.disposalNetEUR ?? ds?.nftTotals?.disposalEUR ?? 0),
      otherIncome: Number(ds?.nftTotals?.incomeEUR || 0),
      total: Number(
        (ds?.nftTotals?.disposalNetEUR ?? ds?.nftTotals?.disposalEUR ?? 0) +
          (ds?.nftTotals?.incomeEUR || 0)
      ),
      disposalShortTermNetEUR: Number(ds?.nftTotals?.disposalShortTermNetEUR || 0),
      disposalLongTermNetEUR: Number(ds?.nftTotals?.disposalLongTermNetEUR || 0),
    },
    exchange: buildProfitBlock(ds?.exchangeTotals),
    liquidity: buildLiquidityBlock(ds?.liquidityTotals),
    selectedCountry,
    selectedYear,
    isPreviewMode: false,
  };

  return {
    datasetId: datasetMeta?.id || root?.datasetId || root?.purchaseId || null,
    selectedCountry,
    selectedYear,
    userWallet: fallbackWallet,
    lastCalculatedAt:
      root?.updatedAt ||
      payload?.snapshotMeta?.updatedAt ||
      payload?.snapshotMeta?.createdAt ||
      ds?.updatedAt ||
      datasetMeta?.updated_at ||
      datasetMeta?.updatedAt ||
      datasetMeta?.created_at ||
      datasetMeta?.createdAt ||
      null,
    dashboardProps: {
      vetTotalProfit: summaryProps.vet,
      vthoTotalProfit: summaryProps.vtho,
      b3trTotalProfit: summaryProps.b3tr,
      nftTotalProfit: Number(summaryProps.nft.swapProfit || 0),
      exchangeTotalProfit: summaryProps.exchange,
      liquidityTotalProfit: summaryProps.liquidity,
      selectedCountry,
      selectedYear,
    },
  };
};

const formatLastUpdated = (value, language) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(language === "de" ? "de-DE" : "en-US");
};

const formatCreatedAt = (value, language) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(language === "de" ? "de-DE" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatShortAddress = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "—";
  if (normalized.length <= 16) return normalized;
  return `${normalized.slice(0, 8)}…${normalized.slice(-6)}`;
};

const formatWalletPreviewAddress = (address) => formatShortAddress(address);

const MOBILE_SMART_SCAN_CORE_REWARD_LINES = [
  {
    field: "vthoGenerated",
    totalFields: ["vthoGenerated", "nodeLegacyRewards", "foundationBonusRewards"],
    key: "vtho-generated",
    labelDe: "Generiertes VTHO",
    labelEn: "VTHO Generated",
    detailDe: "Automatisch generierte VTHO",
    detailEn: "Automatically generated VTHO",
    iconSrc: "/vtho.png",
    iconAlt: "VTHO",
    fallbackIcon: "⚡",
    iconVariant: "token",
    detailBuilder: ({ counts, fmt, isEnglish }) => {
      const parts = [];
      const generated = Number(counts?.vthoGenerated || 0);
      const nodeLegacy = Number(counts?.nodeLegacyRewards || 0);
      const foundation = Number(counts?.foundationBonusRewards || 0);

      if (generated > 0) {
        parts.push(
          `${fmt(generated)} ${isEnglish ? "VTHO generated" : "VTHO generiert"}`
        );
      }

      if (nodeLegacy > 0) {
        parts.push(
          `${fmt(nodeLegacy)} ${
            isEnglish ? "Legacy Node" : "Legacy-Node"
          }`
        );
      }

      if (foundation > 0) {
        parts.push(
          `${fmt(foundation)} ${
            isEnglish ? "Foundation Bonus" : "Foundation-Bonus"
          }`
        );
      }

      return parts.length
        ? parts.join(" · ")
        : isEnglish
          ? "Automatically generated VTHO"
          : "Automatisch generierte VTHO";
    },
  },
];

const getMobileSmartScanActivityCount = (counts, source) => {
  if (!source) return 0;

  if (typeof source === "function") {
    return Number(source(counts) || 0);
  }

  if (Array.isArray(source)) {
    return source.reduce((sum, field) => sum + Number(counts?.[field] || 0), 0);
  }

  return Number(counts?.[source] || 0);
};

/**
 * Singular/Plural für Smart-Scan-Unterzeilen (z. B. „1 Kauf“ / „2 Käufe“).
 * Optional pro Item: labelDeOne, labelEnOne — fehlt es, bleibt die Sammelform (labelDe / labelEn).
 */
const pickSmartScanQuantityLabel = (count, item, isEnglish) => {
  const n = Number(count);
  if (!Number.isFinite(n)) {
    return isEnglish ? item.labelEn : item.labelDe;
  }
  const one = Math.abs(n) === 1;
  if (isEnglish) {
    if (one && item.labelEnOne != null) return item.labelEnOne;
    return item.labelEn;
  }
  if (one && item.labelDeOne != null) return item.labelDeOne;
  return item.labelDe;
};

const MOBILE_SMART_SCAN_DAPP_ACTIVITY_LINES = [
  {
    key: "betterswap",
    labelDe: "BetterSwap",
    labelEn: "BetterSwap",
    iconSrc: "/Betterswap.png",
    iconAlt: "BetterSwap",
    fallbackIcon: "B",
    items: [
      {
        field: "betterSwapPool2EarnRewards",
        labelDe: "Pool2Earn-Belohnungen",
        labelEn: "Pool2Earn rewards",
      },
    ],
  },
  {
    key: "mva",
    labelDe: "MVA",
    labelEn: "MVA",
    iconSrc: "/MVA.jpg",
    iconAlt: "MVA",
    fallbackIcon: "M",
    items: [
      {
        field: "mvaStakingRewards",
        labelDe: "Staking-Belohnungen",
        labelDeOne: "Staking-Belohnung",
        labelEn: "Staking rewards",
        labelEnOne: "Staking reward",
      },
    ],
  },
  {
    key: "safe-haven",
    labelDe: "Safe Haven",
    labelEn: "Safe Haven",
    iconSrc: "/sha.png",
    iconAlt: "Safe Haven",
    fallbackIcon: "S",
    items: [
      {
        field: "safehavenStakingRewards",
        labelDe: "Staking-Belohnungen",
        labelDeOne: "Staking-Belohnung",
        labelEn: "Staking rewards",
        labelEnOne: "Staking reward",
      },
    ],
  },
  {
    key: "golden-empire",
    labelDe: "Golden Empire",
    labelEn: "Golden Empire",
    iconSrc: "/GE.jpg",
    iconAlt: "Golden Empire",
    fallbackIcon: "G",
    items: [
      {
        field: "goldenEmpireRewards",
        labelDe: "Staking-Belohnungen",
        labelDeOne: "Staking-Belohnung",
        labelEn: "Staking rewards",
        labelEnOne: "Staking reward",
      },
      {
        field: "goldenEmpireSpend",
        labelDe: "Ausgaben",
        labelDeOne: "Ausgabe",
        labelEn: "Spend",
        labelEnOne: "Spend event",
      },
      {
        field: "goldenEmpireBurnSpend",
        labelDe: "Burn-Ausgaben",
        labelDeOne: "Burn-Ausgabe",
        labelEn: "Burn Spend",
        labelEnOne: "Burn spend",
      },
    ],
  },
  {
    key: "nfbc",
    labelDe: "NFBC",
    labelEn: "NFBC",
    iconSrc: "/nfbc.png",
    iconAlt: "NFBC",
    fallbackIcon: "N",
    items: [
      {
        field: "nfbcPurchases",
        labelDe: "Käufe",
        labelDeOne: "Kauf",
        labelEn: "Purchases",
        labelEnOne: "Purchase",
      },
      {
        field: "listen2EarnRewards",
        labelDe: "Listen2Earn-Belohnungen",
        labelDeOne: "Listen2Earn-Belohnung",
        labelEn: "Listen2Earn rewards",
        labelEnOne: "Listen2Earn reward",
      },
    ],
  },
  {
    key: "pause-your-carbon",
    labelDe: "Pause Your Carbon",
    labelEn: "Pause Your Carbon",
    iconSrc: "/carbon.png",
    iconAlt: "Pause Your Carbon",
    fallbackIcon: "C",

    // Badge rechts: PYC-Aktivität (Swaps, sonst Events), nicht CY-Menge.
    valueGetter: (counts) =>
      Number(counts?.pauseYourCarbonSwaps || counts?.pauseYourCarbonEvents || 0),

    // Detail immer aus denselben Counts: zuerst Swaps, dann gerundete CY-Menge.
    detailBuilder: ({ counts, fmt, isEnglish }) => {
      const parts = [];
      const swaps = Number(counts?.pauseYourCarbonSwaps || 0);
      const cy = Math.round(Number(counts?.pauseYourCarbonCyAmount || 0));
      if (swaps > 0) {
        parts.push(
          `${fmt(swaps)} ${
            isEnglish
              ? swaps === 1
                ? "PYC swap"
                : "PYC swaps"
              : swaps === 1
                ? "PYC-Swap"
                : "PYC-Swaps"
          }`
        );
      }
      if (cy > 0) {
        parts.push(
          `${fmt(cy)} ${isEnglish ? "CY received" : "CY erhalten"}`
        );
      }
      return parts.length
        ? parts.join(" · ")
        : isEnglish
          ? "Activity detected"
          : "Aktivität erkannt";
    },

    items: [],
  },
  {
    key: "solarwise",
    labelDe: "SolarWise",
    labelEn: "SolarWise",
    iconSrc: "/solarwise.png",
    iconAlt: "SolarWise",
    fallbackIcon: "☀",
    valueField: "solarwiseEvents",
    items: [
      {
        field: "solarwiseStakingRewards",
        labelDe: "Staking-Belohnungen",
        labelDeOne: "Staking-Belohnung",
        labelEn: "Staking rewards",
        labelEnOne: "Staking reward",
      },
      {
        field: "solarwiseStakeDeposits",
        labelDe: "Einzahlungen",
        labelDeOne: "Einzahlung",
        labelEn: "Deposits",
        labelEnOne: "Deposit",
      },
      {
        field: "solarwiseStakeWithdrawals",
        labelDe: "Auszahlungen",
        labelDeOne: "Auszahlung",
        labelEn: "Withdrawals",
        labelEnOne: "Withdrawal",
      },
    ],
  },
  {
    key: "vestation",
    labelDe: "VeStation",
    labelEn: "VeStation",
    iconSrc: "/vestation.png",
    iconAlt: "VeStation",
    fallbackIcon: "V",
    valueField: "vestationEvents",
    items: [
      {
        field: "vestationRewards",
        labelDe: "Belohnungen",
        labelDeOne: "Belohnung",
        labelEn: "Rewards",
        labelEnOne: "Reward",
      },
      {
        field: "vestationDeposits",
        labelDe: "Einzahlungen",
        labelDeOne: "Einzahlung",
        labelEn: "Deposits",
        labelEnOne: "Deposit",
      },
      {
        field: "vestationWithdrawals",
        labelDe: "Auszahlungen",
        labelDeOne: "Auszahlung",
        labelEn: "Withdrawals",
        labelEnOne: "Withdrawal",
      },
    ],
  },
  {
    key: "vimworld",
    labelDe: "VIMworld",
    labelEn: "VIMworld",
    iconSrc: "/VIM.png",
    iconAlt: "VIMworld",
    fallbackIcon: "V",
    valueField: "vimworldEvents",
    items: [
      {
        field: "vimworldRewards",
        labelDe: "Belohnungen",
        labelDeOne: "Belohnung",
        labelEn: "Rewards",
        labelEnOne: "Reward",
      },
      {
        field: "vimworldDeposits",
        labelDe: "Einzahlungen",
        labelDeOne: "Einzahlung",
        labelEn: "Deposits",
        labelEnOne: "Deposit",
      },
      {
        field: "vimworldWithdrawals",
        labelDe: "Auszahlungen",
        labelDeOne: "Auszahlung",
        labelEn: "Withdrawals",
        labelEnOne: "Withdrawal",
      },
      {
        field: "vimworldFees",
        labelDe: "Gebühren",
        labelDeOne: "Gebühr",
        labelEn: "Fees",
        labelEnOne: "Fee",
      },
    ],
  },
  {
    key: "stargate",
    labelDe: "Stargate",
    labelEn: "Stargate",
    iconSrc: ({ highestStargateTier }) =>
      highestStargateTier?.icon || "/nft-icons/Dawn.png",
    iconAlt: ({ highestStargateTier }) =>
      highestStargateTier?.label || "Stargate",
    fallbackIcon: "🚀",
    valueField: "stargateEvents",
    badgeBuilder: ({ isEnglish, highestStargateTier }) =>
      highestStargateTier
        ? `${isEnglish ? "Highest Stargate" : "Höchstes Stargate"}: ${highestStargateTier.label}`
        : null,
    items: [
      {
        field: "stargateStakingRewards",
        labelDe: "Staking-Belohnungen",
        labelDeOne: "Staking-Belohnung",
        labelEn: "Staking rewards",
        labelEnOne: "Staking reward",
      },
      {
        field: "stargateDeposits",
        labelDe: "NFT-Einzahlungen",
        labelDeOne: "NFT-Einzahlung",
        labelEn: "NFT deposits",
        labelEnOne: "NFT deposit",
      },
      {
        field: "stargateWithdrawals",
        labelDe: "NFT-Auszahlungen",
        labelDeOne: "NFT-Auszahlung",
        labelEn: "NFT withdrawals",
        labelEnOne: "NFT withdrawal",
      },
      {
        countGetter: (counts) =>
          Math.max(
            0,
            Number(counts?.stargateMoves || 0) -
              Number(counts?.stargateDeposits || 0) -
              Number(counts?.stargateWithdrawals || 0)
          ),
        labelDe: "NFT-Ein-/Auszahlungen",
        labelDeOne: "NFT-Ein-/Auszahlung",
        labelEn: "NFT deposits/withdrawals",
        labelEnOne: "NFT deposit/withdrawal",
      },
    ],
  },
  {
    key: "thorhead",
    labelDe: "Thorhead",
    labelEn: "Thorhead",
    iconSrc: "/thorhead.jpg",
    iconAlt: "Thorhead",
    fallbackIcon: "⚡",
    items: [
      {
        field: "thorheadStakingRewards",
        labelDe: "Staking-Belohnungen",
        labelDeOne: "Staking-Belohnung",
        labelEn: "Staking rewards",
        labelEnOne: "Staking reward",
      },
    ],
  },
  {
    key: "sagaz",
    labelDe: "Sagaz",
    labelEn: "Sagaz",
    iconSrc: "/sagaz.jpg",
    iconAlt: "Sagaz",
    fallbackIcon: "S",
    items: [
      {
        field: "sagazRewards",
        labelDe: "Belohnungs-Claims",
        labelDeOne: "Belohnungs-Claim",
        labelEn: "Reward claims",
        labelEnOne: "Reward claim",
      },
    ],
  },
  {
    key: "vesea",
    labelDe: "VeSea",
    labelEn: "VeSea",
    iconSrc: "/VSEA.png",
    iconAlt: "VeSea",
    fallbackIcon: "V",
    items: [
      {
        field: "veSeaRewards",
        labelDe: "NFT-Staking-Belohnungen",
        labelDeOne: "NFT-Staking-Belohnung",
        labelEn: "NFT staking rewards",
        labelEnOne: "NFT staking reward",
      },
    ],
  },
  {
    key: "vexchange",
    labelDe: "Vexchange",
    labelEn: "Vexchange",
    iconSrc: "/Vexchange.png",
    iconAlt: "Vexchange",
    fallbackIcon: "V",
    items: [
      {
        field: "vexRewards",
        labelDe: "Farm-Belohnungen",
        labelDeOne: "Farm-Belohnung",
        labelEn: "Farm rewards",
        labelEnOne: "Farm reward",
      },
    ],
  },
  {
    key: "vfox-alliance",
    labelDe: "VFox Alliance",
    labelEn: "VFox Alliance",
    iconSrc: "/VFA.jpg",
    iconAlt: "VFox Alliance",
    fallbackIcon: "V",
    items: [
      {
        field: "vfoxAllianceRewards",
        labelDe: "Belohnungen für NFT-Inhaber",
        labelDeOne: "Belohnung für NFT-Inhaber",
        labelEn: "NFT holder rewards",
        labelEnOne: "NFT holder reward",
      },
    ],
  },
  {
    key: "world-of-v",
    labelDe: "World of V",
    labelEn: "World of V",
    iconSrc: "/wov.png",
    iconAlt: "World of V",
    fallbackIcon: "W",
    items: [
      {
        field: "wovStakingRewards",
        labelDe: "WoV-Staking-Belohnungen",
        labelDeOne: "WoV-Staking-Belohnung",
        labelEn: "WoV staking rewards",
        labelEnOne: "WoV staking reward",
      },
      {
        field: "genesisStakingRewards",
        labelDe: "Genesis-Staking-Belohnungen",
        labelDeOne: "Genesis-Staking-Belohnung",
        labelEn: "Genesis staking rewards",
        labelEnOne: "Genesis staking reward",
      },
    ],
  },
  {
    key: "verocket",
    labelDe: "VeRocket",
    labelEn: "VeRocket",
    iconSrc: "/VeRocket.png",
    iconAlt: "VeRocket",
    fallbackIcon: "R",
    items: [
      {
        field: "verocketRewards",
        labelDe: "VTHO-Claims",
        labelDeOne: "VTHO-Claim",
        labelEn: "VTHO claims",
        labelEnOne: "VTHO claim",
      },
    ],
  },
  {
    key: "juicyfinance",
    labelDe: "JuicyFinance",
    labelEn: "JuicyFinance",
    iconSrc: "/JF.png",
    iconAlt: "JuicyFinance",
    fallbackIcon: "🍓",
    valueField: "juicyFinanceEvents",
    items: [
      {
        field: "juicyFinanceDeposit",
        labelDe: "Einzahlungen (Supply)",
        labelDeOne: "Einzahlung (Supply)",
        labelEn: "Supplies",
        labelEnOne: "Supply",
      },
      {
        field: "juicyFinanceWithdrawal",
        labelDe: "Abhebungen",
        labelDeOne: "Abhebung",
        labelEn: "Withdrawals",
        labelEnOne: "Withdrawal",
      },
      {
        field: "juicyFinanceBorrow",
        labelDe: "Aufnahmen (Borrow)",
        labelDeOne: "Aufnahme (Borrow)",
        labelEn: "Borrows",
        labelEnOne: "Borrow",
      },
      {
        field: "juicyFinanceRepay",
        labelDe: "Rückzahlungen",
        labelDeOne: "Rückzahlung",
        labelEn: "Repayments",
        labelEnOne: "Repayment",
      },
      {
        field: "juicyFinanceLending",
        labelDe: "Sonstige Kreditaktivitäten",
        labelDeOne: "Sonstige Kreditaktivität",
        labelEn: "Other lending activities",
        labelEnOne: "Other lending activity",
      },
    ],
  },
];

const MOBILE_SMART_SCAN_LINE_PRIORITY = {
  // Immer zuerst: generische DEX-/Token-Swaps
  swaps: 10,

  // Kern-Steuer-UI: NFTs, dann Sammel-Rewards/X2Earn, dann Stargate
  nft: 20,
  rewards: 30,
  stargate: 40,

  // VTHO-Erzeugung, dann Liquidität, dann SolarWise als häufige Ökosystem-Signale
  "vtho-generated": 50,
  liquidity: 60,
  solarwise: 70,

  // Weitere dApps (Reihenfolge bewusst unter SolarWise)
  betterswap: 80,
  thorhead: 90,
  juicyfinance: 100,
  mva: 110,
  "world-of-v": 120,
  "pause-your-carbon": 130,
  nfbc: 140,
  "golden-empire": 150,
  "safe-haven": 160,
  vestation: 170,
  vimworld: 180,
  vexchange: 190,
  verocket: 200,
  vesea: 210,
  "vfox-alliance": 220,
  sagaz: 230,
};

const orderMobileSmartScanLines = (lines = []) =>
  lines
    .map((line, index) => ({ line, index }))
    .sort((a, b) => {
      const priorityA = MOBILE_SMART_SCAN_LINE_PRIORITY[a.line?.key] ?? 1000;
      const priorityB = MOBILE_SMART_SCAN_LINE_PRIORITY[b.line?.key] ?? 1000;

      if (priorityA !== priorityB) return priorityA - priorityB;

      return a.index - b.index;
    })
    .map(({ line }) => line);

async function copyTextToClipboard(text) {
  const value = String(text || "").trim();
  if (!value) return false;
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Fallback below
  }
  let textarea = null;
  try {
    if (typeof document === "undefined") return false;
    textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    textarea.style.top = "0";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, value.length);
    return Boolean(document.execCommand("copy"));
  } catch {
    return false;
  } finally {
    textarea?.parentNode?.removeChild(textarea);
  }
}

const formatCompactNumber = (value) =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const withTimeout = async (promise, timeoutMs, label = "operation") => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(`${label} timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    clearTimeout(timer);
  }
};

const unlockMobileWebViewInteraction = () => {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  const body = document.body;
  for (const el of [html, body]) {
    if (!el) continue;
    el.style.overflow = "";
    el.style.overflowX = "";
    el.style.overflowY = "";
    el.style.position = "";
    el.style.top = "";
    el.style.left = "";
    el.style.right = "";
    el.style.width = "";
    el.style.height = "";
    el.style.pointerEvents = "";
    el.style.touchAction = "";
    el.removeAttribute("data-scroll-locked");
    el.removeAttribute("data-scroll-lock");
    el.removeAttribute("data-remove-scroll-bar");
  }
  document
    .querySelectorAll("[data-scroll-locked], [data-scroll-lock], [data-remove-scroll-bar]")
    .forEach((el) => {
      el.removeAttribute("data-scroll-locked");
      el.removeAttribute("data-scroll-lock");
      el.removeAttribute("data-remove-scroll-bar");
      el.style.pointerEvents = "";
    });
  window.dispatchEvent(new Event("resize"));
};

const getVeChainExplorerAddressUrl = (address) => {
  if (!address || typeof address !== "string") return null;
  return `${MOBILE_VECHAIN_EXPLORER_ACCOUNT_URL}${address}`;
};


const getVeWorldRuntimeFlags = () => {
  if (typeof window === "undefined") {
    return {
      isEmbeddedVeWorld: false,
      canUseDirectEmbeddedConnect: false,
      debug: {},
    };
  }

  let forcedEmbedded = false;
  let forcedDirect = false;
  let forcedDisabled = false;

  try {
    const params = new URLSearchParams(window.location.search || "");
    const embeddedFlag =
      params.get("veworldEmbedded") ||
      params.get("embeddedVeWorld") ||
      params.get("embedded");
    const directFlag =
      params.get("directVeWorld") ||
      params.get("veworldDirect") ||
      params.get("forceVeWorldDirect");

    if (
      embeddedFlag === "1" ||
      embeddedFlag === "true" ||
      embeddedFlag === "veworld"
    ) {
      forcedEmbedded = true;
    }

    if (directFlag === "1" || directFlag === "true") {
      forcedDirect = true;
    }

    if (embeddedFlag === "0" || embeddedFlag === "false") {
      forcedDisabled = true;
    }
  } catch {
    // ignore malformed URL state
  }

  const vechain = window.vechain;
  const veworld = window.veworld;
  const hasInjectedLanguage = typeof vechain?.acceptLanguage === "string";
  const hasInjectedProvider = !!vechain || !!veworld || typeof window.connex !== "undefined";
  const referrer = String(document?.referrer || "").toLowerCase();
  const userAgent = String(navigator?.userAgent || "").toLowerCase();
  const hasVeWorldReferrer = referrer.includes("veworld");
  const hasVeWorldUserAgent = userAgent.includes("veworld");
  const hasRealVeWorldRuntime =
    hasInjectedLanguage ||
    hasInjectedProvider ||
    hasVeWorldReferrer ||
    hasVeWorldUserAgent;

  const isEmbeddedVeWorld = forcedDisabled
    ? false
    : forcedEmbedded || hasRealVeWorldRuntime;

  return {
    isEmbeddedVeWorld,
    canUseDirectEmbeddedConnect: forcedDisabled
      ? false
      : hasRealVeWorldRuntime || forcedDirect,
    debug: {
      forcedEmbedded,
      forcedDirect,
      hasInjectedLanguage,
      hasInjectedProvider,
      hasVeWorldReferrer,
      hasVeWorldUserAgent,
      hasWindowVechain: !!vechain,
      hasWindowVeworld: !!veworld,
      hasWindowConnex: typeof window.connex !== "undefined",
      referrer,
      userAgent,
    },
  };
};

const normalizeDatasetMeta = (raw) => {
  if (!raw) return null;
  const id = raw.id || raw.datasetId || raw.dataset_id || null;
  const year = raw.year ?? null;
  const country =
    raw.residence_country ||
    raw.country ||
    raw.countryCode ||
    raw.tax_country ||
    raw.selectedCountry ||
    "DE";
  return {
    id,
    year,
    country,
    plan: raw.plan_effective || raw.plan || "Free",
    updated_at: raw.last_calculated_at || raw.lastCalculatedAt || raw.updated_at || raw.updatedAt,
    updatedAt: raw.last_calculated_at || raw.lastCalculatedAt || raw.updated_at || raw.updatedAt,
    created_at: raw.purchase_date || raw.purchaseDate || raw.created_at || raw.createdAt,
    createdAt: raw.purchase_date || raw.purchaseDate || raw.created_at || raw.createdAt,
    raw,
  };
};

const sortDatasetsForMobile = (items) => {
  return [...items].sort((a, b) => {
    const aCalc = new Date(
      a.last_calculated_at || a.lastCalculatedAt || a.purchase_date || a.purchaseDate || 0
    ).getTime();
    const bCalc = new Date(
      b.last_calculated_at || b.lastCalculatedAt || b.purchase_date || b.purchaseDate || 0
    ).getTime();
    return bCalc - aCalc;
  });
};

const getDatasetDisplayTitle = (dataset, language) => {
  if (!dataset) return language === "en" ? "Wallet Set" : "Wallet-Set";
  const setNumber = dataset.walletSetNumber ?? dataset.id;
  return `${language === "en" ? "Wallet Set" : "Wallet-Set"} #${setNumber}`;
};

const getDatasetSubtitle = (dataset, language) => {
  if (!dataset) return "";
  const wallets = Number(dataset.walletCount || 0);
  const country = dataset.residence_country || "DE";
  const year = dataset.year || new Date().getFullYear();
  return `${year} • ${country} • ${wallets} ${language === "en" ? "wallets" : "Wallets"}`;
};

/** Chip-Farben anhand derselben Plan-IDs wie App.jsx (`normalizePlanId`). */
const getPlanToneClasses = (planRaw) => {
  const id = normalizePlanId(planRaw);
  const tones = {
    ultimate: "bg-rose-50 text-rose-700 border-rose-200",
    elite: "bg-amber-50 text-amber-700 border-amber-200",
    pro: "bg-indigo-50 text-indigo-700 border-indigo-200",
    standard: "bg-slate-100 text-slate-700 border-slate-200",
    dev: "bg-violet-50 text-violet-700 border-violet-200",
    free: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  return tones[id] || tones.free;
};

const getUsagePercent = (dataset) => {
  const used = Number(dataset?.total_transactions || dataset?.plan_used || 0);
  const effectivePlan = dataset?.plan_effective || dataset?.plan || "free";
  const limit = resolvePlanTxLimit(effectivePlan, dataset?.plan_limit);
  if (limit == null || isUnlimitedPlan(effectivePlan)) return 0;
  if (!limit || limit <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((used / limit) * 100)));
};

const getSnapshotExists = (dataset) => Boolean(dataset?.snapshot_exists ?? dataset?.snapshotExists);

const MOBILE_WALLET_ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/;

const normalizeDatasetAddress = (value) => {
  const address = String(value || "").trim();
  return MOBILE_WALLET_ADDRESS_RE.test(address) ? address.toLowerCase() : "";
};

const pushDatasetWalletEntry = (entries, address, type = "other", extra = {}) => {
  const normalized = normalizeDatasetAddress(address);
  if (!normalized) return;
  entries.push({
    address: normalized,
    type,
    ...extra,
  });
};

const pushDatasetWalletArray = (entries, values, type) => {
  if (!Array.isArray(values)) return;
  values.forEach((value) => {
    if (typeof value === "string") {
      pushDatasetWalletEntry(entries, value, type);
      return;
    }

    if (value && typeof value === "object") {
      pushDatasetWalletEntry(
        entries,
        value.address ||
          value.wallet ||
          value.wallet_address ||
          value.main_wallet ||
          value.mainWallet ||
          value.delegate_wallet ||
          value.delegateWallet ||
          value.delegate_address ||
          value.delegateAddress,
        value.type || value.wallet_type || value.role || value.kind || type,
        { txCount: value.txCount || value.tx_count || value.transactions }
      );
    }
  });
};

const normalizeWalletType = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");

const getWalletEntries = (dataset) => {
  const entries = [];
  const raw = dataset?.raw || {};

  pushDatasetWalletArray(entries, dataset?.wallets, "other");
  pushDatasetWalletArray(entries, raw?.wallets, "other");

  pushDatasetWalletArray(entries, dataset?.mainWallets, "main");
  pushDatasetWalletArray(entries, dataset?.main_wallets, "main");
  pushDatasetWalletArray(entries, dataset?.mainAddresses, "main");
  pushDatasetWalletArray(entries, dataset?.main_addresses, "main");
  pushDatasetWalletArray(entries, raw?.mainWallets, "main");
  pushDatasetWalletArray(entries, raw?.main_wallets, "main");
  pushDatasetWalletArray(entries, raw?.mainAddresses, "main");
  pushDatasetWalletArray(entries, raw?.main_addresses, "main");

  pushDatasetWalletArray(entries, dataset?.delegateWallets, "delegate");
  pushDatasetWalletArray(entries, dataset?.delegate_wallets, "delegate");
  pushDatasetWalletArray(entries, dataset?.delegateAddresses, "delegate");
  pushDatasetWalletArray(entries, dataset?.delegate_addresses, "delegate");
  pushDatasetWalletArray(entries, dataset?.veDelegateWallets, "delegate");
  pushDatasetWalletArray(entries, dataset?.ve_delegate_wallets, "delegate");
  pushDatasetWalletArray(entries, raw?.delegateWallets, "delegate");
  pushDatasetWalletArray(entries, raw?.delegate_wallets, "delegate");
  pushDatasetWalletArray(entries, raw?.delegateAddresses, "delegate");
  pushDatasetWalletArray(entries, raw?.delegate_addresses, "delegate");
  pushDatasetWalletArray(entries, raw?.veDelegateWallets, "delegate");
  pushDatasetWalletArray(entries, raw?.ve_delegate_wallets, "delegate");

  pushDatasetWalletArray(entries, dataset?.addresses, "other");
  pushDatasetWalletArray(entries, raw?.addresses, "other");

  pushDatasetWalletEntry(entries, dataset?.main_wallet, "main");
  pushDatasetWalletEntry(entries, dataset?.mainWallet, "main");
  pushDatasetWalletEntry(entries, dataset?.user_wallet, "main");
  pushDatasetWalletEntry(entries, dataset?.userWallet, "main");
  pushDatasetWalletEntry(entries, raw?.main_wallet, "main");
  pushDatasetWalletEntry(entries, raw?.mainWallet, "main");
  pushDatasetWalletEntry(entries, raw?.user_wallet, "main");
  pushDatasetWalletEntry(entries, raw?.userWallet, "main");

  pushDatasetWalletEntry(entries, dataset?.delegate_wallet, "delegate");
  pushDatasetWalletEntry(entries, dataset?.delegateWallet, "delegate");
  pushDatasetWalletEntry(entries, dataset?.delegate_address, "delegate");
  pushDatasetWalletEntry(entries, dataset?.delegateAddress, "delegate");
  pushDatasetWalletEntry(entries, raw?.delegate_wallet, "delegate");
  pushDatasetWalletEntry(entries, raw?.delegateWallet, "delegate");
  pushDatasetWalletEntry(entries, raw?.delegate_address, "delegate");
  pushDatasetWalletEntry(entries, raw?.delegateAddress, "delegate");

  const walletPairs = [
    ...(Array.isArray(dataset?.walletPairs) ? dataset.walletPairs : []),
    ...(Array.isArray(dataset?.wallet_pairs) ? dataset.wallet_pairs : []),
    ...(Array.isArray(raw?.walletPairs) ? raw.walletPairs : []),
    ...(Array.isArray(raw?.wallet_pairs) ? raw.wallet_pairs : []),
  ];

  walletPairs.forEach((pair) => {
    pushDatasetWalletEntry(
      entries,
      pair?.main_wallet || pair?.mainWallet || pair?.main || pair?.address,
      "main"
    );
    pushDatasetWalletEntry(
      entries,
      pair?.delegate_wallet ||
        pair?.delegateWallet ||
        pair?.delegate_address ||
        pair?.delegateAddress ||
        pair?.delegate,
      "delegate"
    );
  });

  const seen = new Set();
  return entries.filter((entry) => {
    const key = `${normalizeWalletType(entry.type)}:${entry.address}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getWalletPanelPairRows = (dataset) =>
  Array.isArray(dataset?.wallets) ? dataset.wallets : [];

const buildExchangeCoverage = (payload) => {
  const rows = Array.isArray(payload?.rows) ? payload.rows : [];
  const uniqueSources = [...new Set(rows.map((row) => row?.source).filter(Boolean))];
  const latestImport =
    rows
      .map((row) => row?.createdAt || row?.timestamp || null)
      .filter(Boolean)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] || null;

  return {
    rowCount: Number(payload?.count || rows.length || 0),
    sourceCount: uniqueSources.length,
    latestImport,
    hasCsv: rows.length > 0,
    mode: rows.length > 0 ? "full" : "wallet-only",
  };
};

const formatCompactDateTime = (value, language) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(language === "de" ? "de-DE" : "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getDatasetYear = (dataset) => {
  const year = Number(dataset?.year);
  return Number.isInteger(year) ? year : null;
};

const getMobileWalletSetYears = (datasets = []) => {
  const currentYear = new Date().getFullYear();
  const defaultYears = [currentYear, currentYear - 1, currentYear - 2];
  const purchasedYears = datasets
    .map(getDatasetYear)
    .filter((year) => Number.isInteger(year) && year >= 2016 && year <= 2100);
  return [...new Set([...defaultYears, ...purchasedYears])]
    .filter((year) => Number.isInteger(year))
    .sort((a, b) => b - a);
};

const getDatasetPlanLimit = (dataset) => {
  const effectivePlan =
    dataset?.plan_effective || dataset?.plan || "free";
  return resolvePlanTxLimit(effectivePlan, dataset?.plan_limit);
};

const formatDatasetPlanLimitLabel = (dataset, language = "de") => {
  const effectivePlan =
    dataset?.plan_effective || dataset?.plan || "free";
  const limit = getDatasetPlanLimit(dataset);
  if (limit == null || isUnlimitedPlan(effectivePlan)) {
    return formatTxLimitDisplay(effectivePlan, language === "en" ? "en" : "de");
  }
  return formatCompactNumber(limit);
};

const getDatasetTxCount = (dataset) =>
  Number(
    dataset?.total_transactions ||
      dataset?.totalTransactions ||
      dataset?.txCount ||
      dataset?.transactionCount ||
      0
  );

const getDatasetWalletCount = (dataset) =>
  Number(
    dataset?.walletCount || getWalletEntries(dataset).length || 0
  );

const getDatasetImportFallbackCount = (dataset) =>
  Number(
    dataset?.exchangeImportCount ||
      dataset?.exchange_import_count ||
      dataset?.csvRowCount ||
      dataset?.csv_row_count ||
      dataset?.importRowCount ||
      dataset?.import_row_count ||
      0
  );

const getDatasetSourceFallbackCount = (dataset) =>
  Number(
    dataset?.exchangeSourceCount ||
      dataset?.exchange_source_count ||
      dataset?.sourceCount ||
      dataset?.source_count ||
      0
  );

const getMobileDatasetCoverage = (dataset, coverage) => {
  const csvRows = Number(
    coverage?.rowCount ?? getDatasetImportFallbackCount(dataset) ?? 0
  );
  const sourceCount = Number(
    coverage?.sourceCount ?? getDatasetSourceFallbackCount(dataset) ?? 0
  );
  const latestImport =
    coverage?.latestImport ||
    dataset?.latestExchangeImportAt ||
    dataset?.latest_exchange_import_at ||
    dataset?.latestImportAt ||
    null;
  return {
    csvRows,
    sourceCount,
    latestImport,
    hasCsv: Boolean(coverage?.hasCsv || csvRows > 0),
  };
};

const getDatasetWalletGroups = (dataset) => {
  const entries = getWalletEntries(dataset);
  const groups = {
    main: [],
    delegate: [],
    other: [],
  };

  entries.forEach((entry) => {
    const address =
      entry?.address ||
      entry?.wallet ||
      entry?.wallet_address ||
      entry?.delegate_address ||
      entry?.main_wallet ||
      "";
    if (!address) return;

    const type = normalizeWalletType(
      entry?.type ||
        entry?.wallet_type ||
        entry?.role ||
        entry?.kind ||
        ""
    );

    const normalized = String(address).trim();

    if (
      type.includes("delegate") ||
      type.includes("vedelegate") ||
      type.includes("reward")
    ) {
      groups.delegate.push(normalized);
      return;
    }

    if (
      type.includes("main") ||
      type.includes("primary") ||
      type.includes("wallet")
    ) {
      groups.main.push(normalized);
      return;
    }

    groups.other.push(normalized);
  });

  const unique = (arr) => [...new Set(arr.filter(Boolean))];

  return {
    main: unique(groups.main),
    delegate: unique(groups.delegate),
    other: unique(groups.other),
    all: unique([...groups.main, ...groups.delegate, ...groups.other]),
  };
};

function MobileYearTabs({ years, selectedYear, onChange }) {
  if (!Array.isArray(years) || years.length === 0) return null;
  return (
    <div className="-mx-1 w-fit max-w-full self-start overflow-x-auto px-1 pb-1">
      <div className="inline-flex gap-2 rounded-[24px] border border-slate-200 bg-white/90 p-1.5 shadow-[0_12px_34px_rgba(15,23,42,0.06)]">
        {years.map((year) => {
          const active = Number(year) === Number(selectedYear);
          return (
            <button
              key={year}
              type="button"
              onClick={() => onChange(Number(year))}
              className={[
                "min-h-[38px] shrink-0 rounded-[18px] px-4 text-sm font-semibold tabular-nums transition",
                active
                  ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-[0_10px_24px_rgba(37,99,235,0.26)]"
                  : "text-slate-600 hover:bg-slate-50",
              ].join(" ")}
              aria-pressed={active}
            >
              {year}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MobileCopyAddressButton({ address, label = "Wallet-Adresse kopieren" }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const ok = await copyTextToClipboard(address);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={[
        "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl transition active:scale-[0.94]",
        copied
          ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
          : "bg-white/45 text-slate-400 ring-1 ring-slate-200/70 hover:bg-white/80 hover:text-slate-600",
      ].join(" ")}
      aria-label={label}
      title={label}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function MobileWalletAddressBadge({ address, tone = "slate" }) {
  const toneClasses =
    tone === "blue"
      ? {
          wrap: "border-blue-100 bg-blue-50/75 text-blue-950",
          icon: "from-blue-600 to-indigo-600 text-white",
        }
      : tone === "violet"
        ? {
            wrap: "border-violet-100 bg-violet-50/75 text-violet-950",
            icon: "from-violet-600 to-fuchsia-600 text-white",
          }
        : {
            wrap: "border-slate-200 bg-slate-50 text-slate-900",
            icon: "from-slate-700 to-slate-950 text-white",
          };

  return (
    <div
      className={[
        "flex items-center gap-3 rounded-[20px] border py-3 pl-3 pr-2.5 shadow-sm",
        toneClasses.wrap,
      ].join(" ")}
      title={address}
    >
      <span
        className={[
          "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br shadow-sm",
          toneClasses.icon,
        ].join(" ")}
      >
        <Link2 className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1 truncate font-mono text-[13px] font-semibold leading-5 tracking-[-0.01em]">
        {formatWalletPreviewAddress(address)}
      </span>
      <MobileCopyAddressButton address={address} />
    </div>
  );
}

function MobileWalletAddressGroup({
  title,
  addresses,
  tone = "slate",
  emptyLabel = "—",
}) {
  const count = Array.isArray(addresses) ? addresses.length : 0;

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          {title}
        </div>
        <div className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold tabular-nums text-slate-500">
          {count}
        </div>
      </div>
      {count ? (
        <div className="space-y-2">
          {addresses.map((address) => (
            <MobileWalletAddressBadge
              key={`${title}-${address}`}
              address={address}
              tone={tone}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-[20px] border border-dashed border-slate-200 bg-white/70 px-3 py-3 text-sm text-slate-400">
          {emptyLabel}
        </div>
      )}
    </div>
  );
}

function MobileWalletSetCard({
  dataset,
  language,
  coverage,
  onOpen,
}) {
  const snapshotExists = getSnapshotExists(dataset);
  const usage = getUsagePercent(dataset);
  const title = getDatasetDisplayTitle(dataset, language);
  const plan = dataset.plan_effective || dataset.plan || "Free";
  const walletCount = getDatasetWalletCount(dataset);
  const txCount = getDatasetTxCount(dataset);
  const planLimit = getDatasetPlanLimit(dataset);
  const year = getDatasetYear(dataset) || new Date().getFullYear();
  const country = dataset.residence_country || dataset.country || "DE";
  const dataCoverage = getMobileDatasetCoverage(dataset, coverage);
  const walletGroups = getDatasetWalletGroups(dataset);
  const lastCalculated =
    dataset.last_calculated_at ||
    dataset.lastCalculatedAt ||
    dataset.updated_at ||
    dataset.updatedAt ||
    null;

  return (
    <article className="group relative w-full overflow-hidden rounded-[26px] border border-slate-200/90 bg-white/86 p-4 shadow-sm transition-all duration-300">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-200 to-transparent" />

      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-[18px] font-semibold tracking-tight text-slate-950">
              {title}
            </h3>
            <span
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${getPlanToneClasses(plan)}`}
            >
              <PlanBadge
                planId={plan}
                size="xs"
                variant="compact"
                title={formatPlanDisplay(plan)}
                className="flex-shrink-0"
              />
              <span>{formatPlanDisplay(plan)}</span>
            </span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500">
            <span>{year}</span>
            <span className="h-1 w-1 rounded-full bg-slate-300" />
            <span>{country}</span>
            <span className="h-1 w-1 rounded-full bg-slate-300" />
            <span>
              {formatLastUpdated(lastCalculated, language) ||
                (language === "en" ? "No calculation yet" : "Noch keine Berechnung")}
            </span>
          </div>
        </div>
        <div
          className={`shrink-0 rounded-full border px-3 py-1.5 text-[11px] font-semibold ${
            snapshotExists
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-amber-200 bg-amber-50 text-amber-700"
          }`}
        >
          {snapshotExists
            ? language === "en"
              ? "Results ready"
              : "Ergebnisse verfügbar"
            : language === "en"
              ? "Analysis required"
              : "Berechnung erforderlich"}
        </div>
      </div>

      <div className="relative mt-5 grid grid-cols-2 gap-2.5">
        <MobileMetricTile
          label={language === "en" ? "Wallets" : "Wallets"}
          value={formatCompactNumber(walletCount)}
          hint={language === "en" ? "Included addresses" : "Enthaltene Adressen"}
        />
        <MobileMetricTile
          label={language === "en" ? "Transactions" : "Transaktionen"}
          value={formatCompactNumber(txCount)}
          hint={
            planLimit != null
              ? `${formatCompactNumber(txCount)} / ${formatCompactNumber(planLimit)}`
              : `${formatCompactNumber(txCount)} — ${formatDatasetPlanLimitLabel(dataset, language)}`
          }
        />
        <MobileMetricTile
          label={language === "en" ? "Data" : "Daten"}
          value={
            dataCoverage.hasCsv
              ? language === "en"
                ? "On-chain + CSV"
                : "On-Chain + CSV"
              : language === "en"
                ? "On-chain"
                : "On-Chain"
          }
          hint={
            dataCoverage.hasCsv
              ? language === "en"
                ? `${formatCompactNumber(dataCoverage.csvRows)} CSV rows`
                : `${formatCompactNumber(dataCoverage.csvRows)} CSV-Zeilen`
              : language === "en"
                ? "No exchange CSV"
                : "Keine Exchange-CSV"
          }
        />
        <MobileMetricTile
          label={language === "en" ? "Sources" : "Quellen"}
          value={
            dataCoverage.hasCsv
              ? formatCompactNumber(Math.max(dataCoverage.sourceCount, 1))
              : "—"
          }
          hint={
            dataCoverage.latestImport
              ? formatCompactDateTime(dataCoverage.latestImport, language)
              : language === "en"
                ? "Managed on a computer"
                : "Verwaltung am PC oder Laptop"
          }
        />
      </div>

      <div className="relative mt-5 rounded-[26px] border border-slate-200/90 bg-white/78 p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-blue-600">
              {language === "en" ? "Wallet structure" : "Wallet-Struktur"}
            </div>
            <div className="mt-1 text-sm font-semibold text-slate-950">
              {language === "en" ? "Main + VeDelegate wallets" : "Main + VeDelegate Wallets"}
            </div>
          </div>
          <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold tabular-nums text-slate-500">
            {walletGroups.all.length}
          </div>
        </div>
        <div className="space-y-4">
          <MobileWalletAddressGroup
            title={language === "en" ? "Main wallets" : "Main Wallets"}
            addresses={walletGroups.main.length ? walletGroups.main : walletGroups.other}
            tone="blue"
            emptyLabel={language === "en" ? "No main wallet" : "Keine Main Wallet"}
          />
          <MobileWalletAddressGroup
            title={language === "en" ? "VeDelegate wallets" : "VeDelegate Wallets"}
            addresses={walletGroups.delegate}
            tone="violet"
            emptyLabel={
              language === "en" ? "No delegate wallet" : "Keine VeDelegate Wallet"
            }
          />
        </div>
      </div>

      {planLimit != null && planLimit > 0 ? (
        <div className="relative mt-5">
          <div className="flex items-center justify-between text-xs font-medium text-slate-500">
            <span>{language === "en" ? "Plan usage" : "Plan-Auslastung"}</span>
            <span>{usage}%</span>
          </div>
          <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-2.5 rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500 transition-all"
              style={{ width: `${Math.min(usage, 100)}%` }}
            />
          </div>
        </div>
      ) : null}

      <div className="relative mt-5 border-t border-slate-100 pt-4">
        <button
          type="button"
          onClick={() => onOpen(dataset)}
          className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(37,99,235,0.24)] transition active:scale-[0.985]"
        >
          <span>{language === "en" ? "View results" : "Ergebnisse anzeigen"}</span>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </article>
  );
}

function MobileResultsMetric({ label, value, hint, icon = null }) {
  return (
    <div className="rounded-[24px] border border-slate-200/80 bg-white/90 px-4 py-3.5 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
            {label}
          </div>
          <div className="mt-1.5 text-[22px] font-bold tracking-[-0.04em] text-slate-950">
            {value}
          </div>
        </div>

        {icon ? (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
            {icon}
          </div>
        ) : null}
      </div>

      {hint ? (
        <div className="mt-1 truncate text-xs font-semibold text-slate-500">
          {hint}
        </div>
      ) : null}
    </div>
  );
}

function MobileWalletSetHero({
  dataset,
  language,
  onBack,
  onStartCalculation,
  calculationState = INITIAL_CALCULATION_STATE,
}) {
  const plan = dataset.plan_effective || dataset.plan || "Free";
  const year = getDatasetYear(dataset) || new Date().getFullYear();
  const country = String(dataset.residence_country || dataset.country || "DE").toUpperCase();
  const title = getDatasetDisplayTitle(dataset, language);
  const walletCount = getDatasetWalletCount(dataset);
  const txCount = getDatasetTxCount(dataset);
  const planLimit = getDatasetPlanLimit(dataset);
  const usage = getUsagePercent(dataset);

  const lastCalculated =
    dataset.last_calculated_at ||
    dataset.lastCalculatedAt ||
    dataset.updated_at ||
    dataset.updatedAt ||
    null;

  const isRunning = Boolean(calculationState?.isRunning);

  const lastCalculatedLabel =
    formatLastUpdated(lastCalculated, language) ||
    (language === "en" ? "Not calculated yet" : "Noch nicht berechnet");

  const planUsageLabel =
    planLimit != null
      ? `${formatCompactNumber(txCount)} / ${formatCompactNumber(planLimit)}`
      : `${formatCompactNumber(txCount)} — ${formatDatasetPlanLimitLabel(dataset, language)}`;

  return (
    <section className="relative overflow-hidden rounded-[34px] border border-blue-100/90 bg-white shadow-[0_22px_60px_rgba(37,99,235,0.10)]">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />
      <div className="pointer-events-none absolute -right-20 -top-24 h-52 w-52 rounded-full bg-blue-100/70 blur-3xl" />
      <div className="pointer-events-none absolute -left-20 bottom-0 h-48 w-48 rounded-full bg-indigo-100/60 blur-3xl" />

      <div className="relative p-5">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex min-h-[42px] items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-bold text-slate-700 shadow-[0_8px_22px_rgba(15,23,42,0.06)] transition active:scale-[0.985]"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>{language === "en" ? "Wallet sets" : "Wallet-Sets"}</span>
          </button>

          <button
            type="button"
            onClick={onStartCalculation}
            disabled={isRunning}
            className="inline-flex min-h-[42px] shrink-0 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-sm font-bold text-white shadow-[0_14px_30px_rgba(37,99,235,0.24)] transition active:scale-[0.985] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isRunning ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span>{language === "en" ? "Recalculate" : "Neu berechnen"}</span>
          </button>
        </div>

        <div className="mt-5 flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-blue-700">
              <Sparkles className="h-3.5 w-3.5" />
              <span>{language === "en" ? "Tax dashboard" : "Steuer-Dashboard"}</span>
            </div>

            <h1 className="mt-3 text-[30px] font-bold leading-[1.02] tracking-[-0.05em] text-slate-950">
              {title}
            </h1>

            <p className="mt-2 text-sm font-semibold leading-5 text-slate-500">
              {language === "en" ? "Tax year" : "Steuerjahr"}{" "}
              <span className="text-slate-800">{year}</span>
              <span className="mx-1.5 text-slate-300">•</span>
              <span className="text-slate-800">{country}</span>
            </p>
          </div>

          <div className="shrink-0 rounded-[22px] border border-amber-200/80 bg-gradient-to-br from-amber-50 to-white px-3.5 py-2.5 shadow-[0_12px_26px_rgba(245,158,11,0.14)]">
            <div className="flex items-center gap-2">
              <PlanBadge
                planId={plan}
                size="xs"
                variant="compact"
                title={formatPlanDisplay(plan)}
                className="flex-shrink-0"
              />
              <span className="max-w-[86px] truncate text-sm font-bold text-slate-950">
                {formatPlanDisplay(plan)}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-[24px] border border-slate-100 bg-slate-50/80 px-4 py-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
            {language === "en" ? "Last calculation" : "Letzte Berechnung"}
          </div>
          <div className="mt-1 text-sm font-semibold text-slate-700">
            {lastCalculatedLabel}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3">
          <MobileResultsMetric
            label={language === "en" ? "Wallets" : "Wallets"}
            value={formatCompactNumber(walletCount)}
            hint={language === "en" ? "Included addresses" : "Enthaltene Adressen"}
            icon={<ShieldCheck className="h-4 w-4" />}
          />

          <MobileResultsMetric
            label={language === "en" ? "Transactions" : "Transaktionen"}
            value={formatCompactNumber(txCount)}
            hint={planUsageLabel}
            icon={<Sparkles className="h-4 w-4" />}
          />
        </div>

        {planLimit != null && planLimit > 0 ? (
          <div className="mt-3 rounded-[26px] border border-slate-200/80 bg-white px-4 py-4 shadow-[0_12px_30px_rgba(15,23,42,0.05)]">
            <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">
              <span>{language === "en" ? "Plan usage" : "Plan-Auslastung"}</span>
              <span className="text-slate-700">{usage}%</span>
            </div>

            <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-2.5 rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500 shadow-[0_0_18px_rgba(99,102,241,0.35)] transition-all"
                style={{ width: `${Math.min(usage, 100)}%` }}
              />
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

const MOBILE_PROGRESS_STEPS = [
  "initializing",
  "fetching",
  "classifying",
  "pricing",
  "fifo",
  "finalizing",
];

function getMobileStepLabel(step, language = "de") {
  const isEnglish = String(language || "").toLowerCase().startsWith("en");
  const labels = {
    initializing: isEnglish ? "Preparing analysis" : "Analyse vorbereiten",
    fetching: isEnglish ? "Fetching transfers" : "Transfers laden",
    classifying: isEnglish ? "Classifying transactions" : "Transaktionen klassifizieren",
    pricing: isEnglish ? "Building price index" : "Preisindex berechnen",
    fifo: isEnglish ? "Running FIFO" : "FIFO berechnen",
    finalizing: isEnglish ? "Finalizing analysis" : "Ergebnisse finalisieren",
    done: isEnglish ? "Ready" : "Fertig",
  };
  return labels[step] || (isEnglish ? "Analysis in progress" : "Analyse läuft");
}

function normalizeMobileProgressDetail(detail, language = "de") {
  const raw = String(detail || "").trim();
  if (!raw) return "";
  const isEnglish = String(language || "").toLowerCase().startsWith("en");
  if (!isEnglish) {
    return translateBackendJobDetailToGerman(raw);
  }
  return translateBackendJobDetailToEnglish(raw);
}

const INITIAL_CALCULATION_STATE = {
  isRunning: false,
  message: "",
  detail: "",
  jobId: null,
  progress: 0,
  currentStep: "initializing",
  completedSteps: [],
  indeterminate: false,
};

const INITIAL_INSTANT_SCAN_STATE = {
  status: "idle", // idle | loading | running | ready | error
  jobId: null,
  progress: 0,
  currentStep: "initializing",
  completedSteps: [],
  message: "",
  detail: "",
  result: null,
  error: null,
  fromSnapshot: false,
  updatedAt: null,
  refreshedAt: null,
};

const getMobileInstantScanCurrentYear = () => new Date().getFullYear();

const getMobileInstantScanYears = () => {
  const currentYear = getMobileInstantScanCurrentYear();
  return [currentYear, currentYear - 1, currentYear - 2].filter(
    (year) => Number.isInteger(year) && year >= 2016
  );
};

const MOBILE_SWAP_ICON_SRC =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
  <defs>
    <linearGradient id="swapBlue" x1="10" y1="8" x2="54" y2="56" gradientUnits="userSpaceOnUse">
      <stop stop-color="#0b5cff"/>
      <stop offset="1" stop-color="#174bd6"/>
    </linearGradient>
  </defs>
  <path
    d="M17 20h25.8l-5.7-5.7a3.2 3.2 0 0 1 4.5-4.5l11.2 11.2a3.2 3.2 0 0 1 0 4.5L41.6 36.7a3.2 3.2 0 1 1-4.5-4.5l5.7-5.7H17a3.25 3.25 0 0 1 0-6.5Z"
    fill="url(#swapBlue)"
  />
  <path
    d="M47 44H21.2l5.7 5.7a3.2 3.2 0 0 1-4.5 4.5L11.2 43a3.2 3.2 0 0 1 0-4.5l11.2-11.2a3.2 3.2 0 1 1 4.5 4.5l-5.7 5.7H47a3.25 3.25 0 0 1 0 6.5Z"
    fill="url(#swapBlue)"
  />
</svg>`);

// TEMP TEST OVERRIDE:
// Für diese Wallet werden Wallet-Sets im Mobile-Flow absichtlich ignoriert,
// damit der Empty-State + Smart Scan getestet werden kann.
// Vor Production entfernen oder auf false setzen.
const MOBILE_SMART_SCAN_FORCE_EMPTY_ENABLED = false;

const MOBILE_SMART_SCAN_FORCE_EMPTY_WALLETS = new Set([
  "0x3c1d3cc2a44569cecbef586c1fcd8cbb821b9c5e",
]);

function shouldForceMobileSmartScanEmpty(walletAddress) {
  if (!MOBILE_SMART_SCAN_FORCE_EMPTY_ENABLED) return false;

  const wallet = String(walletAddress || "").toLowerCase().trim();
  if (!wallet) return false;

  return MOBILE_SMART_SCAN_FORCE_EMPTY_WALLETS.has(wallet);
}

function DatasetCalculationStatus({
  language,
  hasCsv,
  progress = 0,
  currentStep = "initializing",
  completedSteps = [],
  message = "",
  detail = "",
  indeterminate = false,
}) {
  const isEnglish = String(language || "").toLowerCase().startsWith("en");
  const pct = Math.max(0, Math.min(100, Math.round(progress)));
  const completedCount = MOBILE_PROGRESS_STEPS.filter((step) =>
    completedSteps.includes(step)
  ).length;
  const activeIndex = Math.max(
    0,
    MOBILE_PROGRESS_STEPS.findIndex((step) => step === currentStep)
  );

  return (
    <section className="rounded-[24px] border border-blue-200 bg-[linear-gradient(180deg,rgba(239,246,255,0.96)_0%,rgba(255,255,255,1)_100%)] p-4 shadow-[0_12px_28px_rgba(59,130,246,0.10)]">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-[0_12px_24px_rgba(37,99,235,0.18)]">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">
                {message || (isEnglish ? "Dataset is being updated" : "Dataset wird aktualisiert")}
              </div>
              <div className="mt-1 text-xs leading-relaxed text-slate-500">
                {hasCsv
                  ? isEnglish
                    ? "Using on-chain data and saved exchange CSV imports."
                    : "On-Chain-Daten und gespeicherte Exchange-CSVs werden verwendet."
                  : isEnglish
                    ? "Using on-chain data only."
                    : "Es werden nur On-Chain-Daten verwendet."}
              </div>
            </div>
            <div className="shrink-0 rounded-full border border-blue-200 bg-white px-3 py-1 text-xs font-semibold tabular-nums text-blue-700 shadow-sm">
              {indeterminate ? (isEnglish ? "Loading…" : "Lädt…") : `${pct}%`}
            </div>
          </div>

          <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-blue-100">
            {indeterminate ? (
              <div className="h-2.5 w-[34%] animate-pulse rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />
            ) : (
              <div
                className="h-2.5 rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500 transition-[width] duration-300 ease-out"
                style={{ width: `${pct}%` }}
              />
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="rounded-full bg-blue-50 px-3 py-1 text-[11px] font-semibold text-blue-700">
              {getMobileStepLabel(currentStep, language)}
            </div>
            <div className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-600">
              {(isEnglish ? "Step" : "Schritt")}{" "}
              {Math.min(activeIndex + 1, MOBILE_PROGRESS_STEPS.length)}/{MOBILE_PROGRESS_STEPS.length}
            </div>
            <div className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-600">
              {(isEnglish ? "Done" : "Fertig")} {completedCount}/{MOBILE_PROGRESS_STEPS.length}
            </div>
          </div>

          {detail ? (
            <div className="mt-2 text-xs text-slate-500">{detail}</div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function MobileScanIcon({
  src,
  fallback = "✦",
  alt = "",
  className = "",
  variant = "image",
}) {
  const normalizedSrc = String(src || "").trim();
  const [failed, setFailed] = useState(false);
  const isSwap = variant === "swap";
  const isToken = variant === "token";

  // Wichtig: Wenn sich das Icon ändert, darf ein früherer Ladefehler
  // nicht weiter gecacht bleiben. Sonst bleibt z. B. earth.png unsichtbar.
  useEffect(() => {
    setFailed(false);
  }, [normalizedSrc]);

  return (
    <span
      className={[
        "inline-flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl",
        isSwap
          ? "border border-white/80 bg-white shadow-[0_12px_26px_rgba(15,23,42,0.10)]"
          : "border-0 bg-transparent shadow-none",
        className,
      ].join(" ")}
    >
      {normalizedSrc && !failed ? (
        <img
          src={normalizedSrc}
          alt={alt}
          className={
            isSwap
              ? "h-full w-full object-contain p-1.5"
              : isToken
                ? "h-full w-full origin-center scale-[1.18] object-contain object-center"
                : "h-full w-full object-contain object-center"
          }
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="text-lg font-semibold text-slate-700">{fallback}</span>
      )}
    </span>
  );
}

/** Einzelne Slug-Segmente für otherRewardBreakdown ins Deutsche (Smart Scan). */
const MOBILE_REWARD_CLASS_SEGMENT_DE = {
  reward: "Belohnung",
  rewards: "Belohnungen",
  pool: "Pool",
  staking: "Staking",
  governance: "Governance",
  nft: "NFT",
  mint: "Mint",
  mints: "Mints",
  sale: "Verkauf",
  sales: "Verkäufe",
  claim: "Claim",
  claims: "Claims",
  distribution: "Auszahlung",
  x2earn: "X2Earn",
  b3tr: "B3TR",
  vtho: "VTHO",
  vet: "VET",
  defi: "DeFi",
  swap: "Swap",
  swaps: "Swaps",
  liquidity: "Liquidität",
  deposit: "Einzahlung",
  deposits: "Einzahlungen",
  withdrawal: "Auszahlung",
  withdrawals: "Auszahlungen",
  fee: "Gebühr",
  fees: "Gebühren",
  other: "Sonstige",
  unknown: "Unbekannt",
  generic: "Allgemein",
  transfer: "Transfer",
  airdrop: "Airdrop",
  bridge: "Bridge",
  lending: "Kredit",
  borrow: "Kreditaufnahme",
  repay: "Rückzahlung",
  supply: "Supply",
  farm: "Farm",
  node: "Node",
  legacy: "Legacy",
  foundation: "Foundation",
  bonus: "Bonus",
  token: "Token",
  tokens: "Tokens",
  payout: "Auszahlung",
  payouts: "Auszahlungen",
  income: "Einkommen",
};

/** Klassifikations-Slug für Smart-Scan-Detail lesbar machen (z. B. b3tr_pool_reward). */
function humanizeMobileScanRewardClass(cls, isEnglish) {
  const raw = String(cls || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  if (!raw) return "?";
  if (isEnglish) {
    return raw.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
  }
  const words = raw.split("_").filter(Boolean);
  const mapped = words.map((seg) => {
    if (/^\d+$/.test(seg)) return seg;
    const w = MOBILE_REWARD_CLASS_SEGMENT_DE[seg];
    if (w) return w;
    return seg.replace(/\b\w/g, (ch) => ch.toUpperCase());
  });
  return mapped.join(" ");
}

function MobileInstantScanResultCard({ language, result }) {
  const isEnglish = String(language || "").toLowerCase().startsWith("en");
  const fmt = (n) => Number(n || 0).toLocaleString(isEnglish ? "en-US" : "de-DE");

  const fmtAmount = (n) => {
    const value = Number(n || 0);
    return value.toLocaleString(isEnglish ? "en-US" : "de-DE", {
      minimumFractionDigits: 0,
      maximumFractionDigits: value >= 1000 ? 2 : 4,
    });
  };

  const resultsByYear =
    result && typeof result.resultsByYear === "object" && result.resultsByYear
      ? result.resultsByYear
      : null;

  const availableYears = (() => {
    const input =
      Array.isArray(result?.years) && result.years.length
        ? result.years
        : Object.keys(resultsByYear || {}).sort((a, b) => Number(b) - Number(a));
    const seen = new Set();
    const out = [];
    for (const value of input) {
      const year = Number(value);
      if (!Number.isInteger(year)) continue;
      if (seen.has(year)) continue;
      seen.add(year);
      out.push(year);
    }
    return out;
  })();

  const currentScanYear = getMobileInstantScanCurrentYear();
  const defaultYear = availableYears.includes(currentScanYear)
    ? currentScanYear
    : availableYears[0] || Number(result?.currentYear || result?.year) || currentScanYear;

  const [selectedYear, setSelectedYear] = useState(defaultYear);

  useEffect(() => {
    if (!availableYears.length) return;
    if (!availableYears.includes(selectedYear)) {
      setSelectedYear(defaultYear);
    }
  }, [availableYears, defaultYear, selectedYear]);

  if (!result) return null;

  // Wenn das Backend pro-Jahr-Buckets liefert, nutzen wir den ausgewählten
  // Jahres-Bucket; sonst Backwards-Compat: Top-Level-Counts.
  const yearResult = resultsByYear?.[selectedYear] || null;
  const counts = yearResult || result;
  const headerYear = Number(counts?.year || selectedYear || result?.year || currentScanYear);

  const highestStargateTier = counts?.highestStargateTier || null;
  const currentYearGalaxyTier = isRealMobileGalaxyTier(counts?.highestGalaxyTier)
    ? counts.highestGalaxyTier
    : null;
  const lastLiquidityDex = counts?.lastLiquidityDex || null;

  // Historischer Galaxy-Fallback:
  // - Wenn im gewählten Jahr ein Galaxy-Upgrade erkannt wurde: dieses Jahr anzeigen.
  // - Wenn im gewählten Jahr keins erkannt wurde: letztes hochwertigstes aus älteren Jahren anzeigen.
  // - Wenn nie eins erkannt wurde: Earth anzeigen.
  const getHighestGalaxyTierForYear = (year) => {
    const tier = resultsByYear?.[year]?.highestGalaxyTier || null;
    return isRealMobileGalaxyTier(tier) ? tier : null;
  };

  const getHistoricalGalaxyTierFallback = (forYear) => {
    const numericForYear = Number(forYear);

    const numericYears = [...availableYears]
      .map((y) => Number(y))
      .filter((y) => Number.isInteger(y))
      .sort((a, b) => b - a);

    const exact = getHighestGalaxyTierForYear(numericForYear);
    if (exact) return exact;

    for (const year of numericYears) {
      if (year > numericForYear) continue;

      const candidate = getHighestGalaxyTierForYear(year);
      if (candidate) return candidate;
    }

    return null;
  };

  const historicalGalaxyTier = getHistoricalGalaxyTierFallback(selectedYear);

  const effectiveGalaxyTier =
    currentYearGalaxyTier || historicalGalaxyTier || MOBILE_EARTH_GALAXY_TIER;

  const galaxyBadge = currentYearGalaxyTier
    ? `${isEnglish ? "Highest Galaxy" : "Höchstes Galaxy"}: ${currentYearGalaxyTier.label}`
    : effectiveGalaxyTier.key === "earth"
      ? isEnglish
        ? "No Galaxy upgrade detected"
        : "Kein Galaxy-Upgrade erkannt"
      : `${isEnglish ? "Last detected Galaxy" : "Zuletzt erkanntes Galaxy"}: ${effectiveGalaxyTier.label}`;

  const nftEventsGalaxyIconSrc = isRealMobileGalaxyTier(effectiveGalaxyTier)
    ? effectiveGalaxyTier.icon
    : MOBILE_GALAXY_FALLBACK_ICON_SRC;

  const lines = [];

  if (counts.swaps > 0) {
    const hasSwapBreakdown =
      Object.prototype.hasOwnProperty.call(counts, "swapIn") &&
      Object.prototype.hasOwnProperty.call(counts, "swapOut");
    const swapIn = Number(counts.swapIn || 0);
    const swapOut = Number(counts.swapOut || 0);

    let swapDetail;
    if (hasSwapBreakdown) {
      const parts = [];
      if (swapOut > 0) {
        parts.push(
          `${fmt(swapOut)} ${
            isEnglish
              ? swapOut === 1
                ? "swap out"
                : "swaps out"
              : swapOut === 1
                ? "Swap-Out"
                : "Swap-Outs"
          }`
        );
      }
      if (swapIn > 0) {
        parts.push(
          `${fmt(swapIn)} ${
            isEnglish
              ? swapIn === 1
                ? "swap in"
                : "swaps in"
              : swapIn === 1
                ? "Swap-In"
                : "Swap-Ins"
          }`
        );
      }
      swapDetail = parts.length
        ? parts.join(" · ")
        : isEnglish
          ? counts.swaps === 1
            ? "Token swap detected"
            : "Token swaps detected"
          : counts.swaps === 1
            ? "Erkannter Token-Swap"
            : "Erkannte Token-Swaps";
    } else {
      swapDetail = isEnglish
        ? counts.swaps === 1
          ? "Token swap detected"
          : "Token swaps detected"
        : counts.swaps === 1
          ? "Erkannter Token-Swap"
          : "Erkannte Token-Swaps";
    }

    lines.push({
      key: "swaps",
      label: isEnglish
        ? counts.swaps === 1
          ? "Swap"
          : "Swaps"
        : counts.swaps === 1
          ? "Token-Swap"
          : "Token-Swaps",
      value: counts.swaps,
      detail: swapDetail,
      iconSrc: MOBILE_SWAP_ICON_SRC,
      iconAlt: "Swaps",
      fallbackIcon: "↔️",
      iconVariant: "swap",
    });
  }

  if (counts.nftEvents > 0) {
    const detailParts = [];
    if (counts.nftMints > 0) {
      detailParts.push(
        `${fmt(counts.nftMints)} ${
          isEnglish
            ? counts.nftMints === 1
              ? "mint"
              : "mints"
            : counts.nftMints === 1
              ? "Prägung"
              : "Prägungen"
        }`
      );
    }
    if (counts.nftSales > 0) {
      detailParts.push(
        `${fmt(counts.nftSales)} ${
          isEnglish
            ? counts.nftSales === 1 ? "sale" : "sales"
            : counts.nftSales === 1 ? "Verkauf" : "Verkäufe"
        }`
      );
    }
    lines.push({
      key: "nft",
      label: isEnglish
        ? counts.nftEvents === 1
          ? "NFT event"
          : "NFT events"
        : counts.nftEvents === 1
          ? "NFT-Event"
          : "NFT-Events",
      value: counts.nftEvents,
      detail: detailParts.length ? detailParts.join(" · ") : null,
      iconSrc: nftEventsGalaxyIconSrc,
      iconAlt: effectiveGalaxyTier?.label || "NFT",
      fallbackIcon: "🖼️",
      badge: galaxyBadge,
    });
  }

  const generalRewardTotal =
    Number(counts.governanceRewards || 0) +
    Number(counts.x2earnDistribution || 0) +
    Number(counts.x2earnClaims || 0) +
    Number(counts.otherRewards || 0);

  if (generalRewardTotal > 0) {
    const detailParts = [];
    if (counts.governanceRewards > 0) {
      detailParts.push(
        `${fmt(counts.governanceRewards)} ${
          isEnglish
            ? counts.governanceRewards === 1
              ? "Governance reward"
              : "Governance rewards"
            : counts.governanceRewards === 1
              ? "Governance-Belohnung"
              : "Governance-Belohnungen"
        }`
      );
    }
    const x2Payouts =
      Number(counts.x2earnDistribution || 0) + Number(counts.x2earnClaims || 0);
    if (x2Payouts > 0) {
      detailParts.push(
        `${fmt(x2Payouts)} ${
          isEnglish
            ? x2Payouts === 1
              ? "X2Earn payout"
              : "X2Earn payouts"
            : x2Payouts === 1
              ? "X2Earn-Auszahlung"
              : "X2Earn-Auszahlungen"
        }`
      );
    }
    if (counts.otherRewards > 0) {
      const breakdown = counts.otherRewardBreakdown;
      const entries =
        breakdown && typeof breakdown === "object"
          ? Object.entries(breakdown).filter(([, n]) => Number(n) > 0)
          : [];
      if (entries.length) {
        entries.sort((a, b) => Number(b[1]) - Number(a[1]));
        const maxParts = 10;
        const shown = entries.slice(0, maxParts);
        const parts = shown.map(
          ([cls, n]) => `${fmt(n)} ${humanizeMobileScanRewardClass(cls, isEnglish)}`
        );
        if (entries.length > maxParts) {
          const hidden = entries.length - maxParts;
          parts.push(
            isEnglish
              ? hidden === 1
                ? "+1 more type"
                : `+${hidden} more types`
              : hidden === 1
                ? "+1 weiterer Typ"
                : `+${hidden} weitere Typen`
          );
        }
        detailParts.push(parts.join(" · "));
      } else {
        detailParts.push(
          `${fmt(counts.otherRewards)} ${
            isEnglish
              ? counts.otherRewards === 1
                ? "other reward"
                : "other rewards"
              : counts.otherRewards === 1
                ? "weitere Belohnung"
                : "weitere Belohnungen"
          }`
        );
      }
    }
    lines.push({
      key: "rewards",
      label: isEnglish
        ? generalRewardTotal === 1
          ? "Reward / X2Earn claim"
          : "Rewards / X2Earn claims"
        : generalRewardTotal === 1
          ? "Belohnung / X2Earn-Claim"
          : "Belohnungen / X2Earn-Claims",
      value: generalRewardTotal,
      detail: detailParts.length ? detailParts.join(" · ") : null,
      iconSrc: "/b3tr.png",
      iconAlt: "B3TR",
      fallbackIcon: "🎁",
    });
  }

  for (const rewardLine of MOBILE_SMART_SCAN_CORE_REWARD_LINES) {
    const value = Array.isArray(rewardLine.totalFields)
      ? rewardLine.totalFields.reduce(
          (sum, field) => sum + Number(counts?.[field] || 0),
          0
        )
      : Number(counts?.[rewardLine.field] || 0);

    if (value <= 0) continue;

    const detail =
      typeof rewardLine.detailBuilder === "function"
        ? rewardLine.detailBuilder({ counts, fmt, isEnglish })
        : isEnglish
          ? rewardLine.detailEn
          : rewardLine.detailDe;

    lines.push({
      key: rewardLine.key,
      label: isEnglish ? rewardLine.labelEn : rewardLine.labelDe,
      value,
      detail,
      iconSrc: rewardLine.iconSrc,
      iconAlt: rewardLine.iconAlt,
      fallbackIcon: rewardLine.fallbackIcon,
      iconVariant: rewardLine.iconVariant || "image",
    });
  }

  for (const appLine of MOBILE_SMART_SCAN_DAPP_ACTIVITY_LINES) {
    const itemParts = [];
    const itemValues = [];

    for (const item of appLine.items || []) {
      const itemValue = getMobileSmartScanActivityCount(
        counts,
        item.countGetter || item.field || item.fields
      );

      if (itemValue <= 0) continue;

      itemValues.push(itemValue);
      const formattedItemValue =
        typeof item.formatter === "function"
          ? item.formatter(itemValue, { fmt, fmtAmount, isEnglish, counts })
          : fmt(itemValue);

      itemParts.push(
        `${formattedItemValue} ${pickSmartScanQuantityLabel(itemValue, item, isEnglish)}`
      );
    }

    const fallbackValue = itemValues.reduce((sum, value) => sum + value, 0);

    const value = appLine.valueField
      ? Number(counts?.[appLine.valueField] || fallbackValue)
      : typeof appLine.valueGetter === "function"
        ? Number(appLine.valueGetter(counts) || fallbackValue)
        : fallbackValue;

    if (value <= 0) continue;

    const context = {
      counts,
      fmt,
      fmtAmount,
      isEnglish,
      highestStargateTier,
    };

    const iconSrc =
      typeof appLine.iconSrc === "function"
        ? appLine.iconSrc(context)
        : appLine.iconSrc;

    const iconAlt =
      typeof appLine.iconAlt === "function"
        ? appLine.iconAlt(context)
        : appLine.iconAlt;

    const badge =
      typeof appLine.badgeBuilder === "function"
        ? appLine.badgeBuilder(context)
        : appLine.badge || null;

    const detail =
      typeof appLine.detailBuilder === "function"
        ? appLine.detailBuilder({ counts, fmt, fmtAmount, isEnglish })
        : itemParts.length
          ? itemParts.join(" · ")
          : isEnglish
            ? "Activity detected"
            : "Aktivität erkannt";

    lines.push({
      key: appLine.key,
      label: isEnglish ? appLine.labelEn : appLine.labelDe,
      value,
      detail,
      iconSrc,
      iconAlt,
      fallbackIcon: appLine.fallbackIcon,
      badge,
      iconVariant: appLine.iconVariant || "image",
    });
  }

  if (counts.liquidityEvents > 0) {
    const hasLiquidityAddRemoveSplit =
      counts.liquidityAdd !== undefined &&
      counts.liquidityRemove !== undefined;
    let breakdown = "";
    if (hasLiquidityAddRemoveSplit) {
      const la = Number(counts.liquidityAdd);
      const lr = Number(counts.liquidityRemove);
      breakdown = [
        `${fmt(la)} ${
          isEnglish
            ? la === 1
              ? "pool add"
              : "pool adds"
            : la === 1
              ? "Einzahlung ins Pool"
              : "Einzahlungen ins Pool"
        }`,
        `${fmt(lr)} ${
          isEnglish
            ? lr === 1
              ? "pool remove"
              : "pool removes"
            : lr === 1
              ? "Entnahme aus Pool"
              : "Entnahmen aus Pool"
        }`,
      ].join(" · ");
    }
    const dexPart = lastLiquidityDex
      ? isEnglish
        ? `Last used DEX: ${lastLiquidityDex.label}`
        : `Zuletzt genutzte DEX: ${lastLiquidityDex.label}`
      : "";
    const detail =
      [breakdown, dexPart].filter(Boolean).join(" · ") ||
      (isEnglish
        ? counts.liquidityEvents === 1
          ? "Pool add/remove activity"
          : "Pool add/remove activities"
        : counts.liquidityEvents === 1
          ? "Pool-Ein- oder -Auszahlung"
          : "Pool-Ein-/Auszahlungen");
    lines.push({
      key: "liquidity",
      label: isEnglish
        ? counts.liquidityEvents === 1
          ? "Liquidity event"
          : "Liquidity events"
        : counts.liquidityEvents === 1
          ? "Liquiditätsereignis"
          : "Liquiditätsereignisse",
      value: counts.liquidityEvents,
      detail,
      iconSrc: lastLiquidityDex?.icon || null,
      iconAlt: lastLiquidityDex?.label || "Liquidity",
      fallbackIcon: "💧",
    });
  }

  const orderedLines = orderMobileSmartScanLines(lines);

  const total = Number(counts.totalRelevantTransactions || 0);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          {isEnglish ? "Tax year" : "Steuerjahr"}
        </div>
        <MobileYearTabs
          years={availableYears.length ? availableYears : [headerYear]}
          selectedYear={selectedYear}
          onChange={setSelectedYear}
        />
      </div>

      {orderedLines.length ? (
        <ul className="divide-y divide-slate-100 rounded-[26px] border border-slate-200/90 bg-white/86 px-4 py-1 shadow-sm">
          {orderedLines.map((line) => (
            <li
              key={line.key}
              className="flex items-start justify-between gap-3 py-3.5 first:pt-3 last:pb-3"
            >
              <div className="flex min-w-0 items-start gap-3">
                <MobileScanIcon
                  key={`${line.key}:${line.iconSrc || "fallback"}`}
                  src={line.iconSrc}
                  alt={line.iconAlt || line.label}
                  fallback={line.fallbackIcon || "✦"}
                  variant={line.iconVariant || "image"}
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{line.label}</div>
                  {line.detail ? (
                    <div className="mt-0.5 text-xs leading-5 text-slate-500">{line.detail}</div>
                  ) : null}
                  {line.badge ? (
                    <div className="mt-2 inline-flex rounded-full border border-slate-200 bg-white/80 px-2.5 py-1 text-[11px] font-semibold text-slate-700 shadow-sm">
                      {line.badge}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="shrink-0 rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold tabular-nums text-slate-800">
                {fmt(line.value)}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="rounded-[26px] border border-slate-200 bg-white/86 px-4 py-4 text-sm leading-6 text-slate-600 shadow-sm">
          {isEnglish
            ? "No potentially tax-relevant on-chain activity was detected for this wallet in the selected tax year."
            : "Für diese Wallet wurden im gewählten Steuerjahr keine potenziell steuerrelevanten On-Chain-Aktivitäten erkannt."}
        </div>
      )}

      <div className="flex items-center justify-between rounded-[24px] border border-blue-100 bg-blue-50/75 px-4 py-3">
        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-700">
          {isEnglish
            ? "Potentially tax-relevant transactions"
            : "Potenziell steuerrelevante Transaktionen"}
        </div>
        <div className="text-lg font-semibold tabular-nums text-slate-950">{fmt(total)}</div>
      </div>
    </div>
  );
}

function MobileDesktopHandoffCard({
  language,
  copied = false,
  sharing = false,
  context,
  onCopyDesktopLink,
  onShareDesktopLink,
  onView,
}) {
  const isEnglish = String(language || "").toLowerCase().startsWith("en");

  useEffect(() => {
    onView?.(context);
  }, [context, onView]);

  const steps = isEnglish
    ? [
        "Open v3tax.app on a PC, Mac, or laptop",
        "Connect the same VeWorld wallet",
        "Start the full tax calculation",
      ]
    : [
        "Öffne v3tax.app auf einem PC oder Laptop",
        "Verbinde dieselbe VeWorld-Wallet",
        "Starte die vollständige Steuerberechnung",
      ];

  return (
    <section className="relative overflow-hidden rounded-[28px] border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-5 shadow-[0_16px_42px_rgba(37,99,235,0.10)]">
      <div className="pointer-events-none absolute -right-16 -top-20 h-44 w-44 rounded-full bg-blue-400/12 blur-3xl" />

      <div className="relative">
        <div className="inline-flex max-w-full items-start gap-2 rounded-full border border-blue-200 bg-white/82 px-3 py-1 text-[11px] font-bold uppercase leading-4 tracking-[0.16em] text-blue-700 shadow-sm">
          <ExternalLink className="mt-px h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 whitespace-normal">
            {isEnglish ? "Full version on a computer" : "Vollversion auf PC oder Laptop"}
          </span>
        </div>

        <h2 className="mt-3 text-xl font-semibold leading-tight tracking-tight text-slate-950">
          {isEnglish
            ? "Complete your tax calculation on a computer"
            : "Vollständige Steuerberechnung am Computer"}
        </h2>

        <p className="mt-3 text-sm leading-6 text-slate-600">
          {isEnglish
            ? "Smart Scan is the mobile preview of your VeChain activity. For the full tax calculation with Wallet Sets, exchange CSVs, FIFO details, and PDF and CSV tax reports, open V3Tax on a PC, Mac, or laptop."
            : "Smart Scan ist die mobile Vorschau deiner VeChain-Aktivitäten. Für die vollständige Steuerberechnung mit Wallet-Sets, Exchange-CSVs, FIFO-Details sowie PDF- und CSV-Steuerreports öffne V3Tax auf einem PC oder Laptop."}
        </p>

        <ol className="mt-4 space-y-2">
          {steps.map((step, index) => (
            <li key={step} className="flex items-start gap-3 text-[13px] leading-5 text-slate-700">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-100 text-[11px] font-bold text-blue-700">
                {index + 1}
              </span>
              <span className="min-w-0 flex-1">{step}</span>
            </li>
          ))}
        </ol>

        <div className="mt-4 rounded-[22px] border border-blue-100 bg-white/86 p-4 text-center shadow-sm">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            {isEnglish ? "Open on your computer" : "Auf deinem PC oder Laptop öffnen"}
          </div>

          <div className="mt-2 select-all break-all font-mono text-[22px] font-black tracking-tight text-blue-700">
            https://v3tax.app
          </div>

          <p className="mx-auto mt-2 max-w-[300px] text-[12px] leading-5 text-slate-500">
            {isEnglish
              ? "Open v3tax.app on your computer and connect the same VeWorld wallet."
              : "Öffne v3tax.app auf deinem Computer und verbinde dort dieselbe VeWorld-Wallet."}
          </p>
        </div>

        <button
          type="button"
          onClick={onShareDesktopLink}
          disabled={sharing}
          className="mt-4 inline-flex min-h-[50px] w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 text-center text-sm font-semibold leading-5 text-white shadow-[0_14px_30px_rgba(37,99,235,0.26)] transition active:scale-[0.985] disabled:cursor-wait disabled:opacity-70"
        >
          {sharing ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          ) : (
            <Send className="h-4 w-4 shrink-0" />
          )}
          <span className="min-w-0 whitespace-normal">
            {isEnglish ? "Send link to computer" : "Link an PC oder Laptop senden"}
          </span>
        </button>

        <button
          type="button"
          onClick={onCopyDesktopLink}
          className="mt-3 inline-flex min-h-[46px] w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-center text-sm font-semibold leading-5 text-slate-700 shadow-sm transition active:scale-[0.985]"
        >
          {copied ? <Check className="h-4 w-4 shrink-0" /> : <Copy className="h-4 w-4 shrink-0" />}
          {copied
            ? isEnglish
              ? "Link copied"
              : "Link kopiert"
            : isEnglish
              ? "Copy link"
              : "Link kopieren"}
        </button>
      </div>
    </section>
  );
}

function MobileSmartScanPanel({
  language,
  instantScanState,
  startMobileInstantScan,
  instantScanStartedRef,
  walletAddress,
  isAdminInstantScan = false,
  adminScanWalletInput = "",
  onAdminScanWalletInputChange,
  desktopLinkCopied = false,
  desktopLinkSharing = false,
  onCopyDesktopLink,
  onShareDesktopLink,
  onDesktopHandoffView,
}) {
  const isEnglish = String(language || "").toLowerCase().startsWith("en");
  const isBusy =
    instantScanState.status === "loading" || instantScanState.status === "running";
  const progress = Math.max(0, Math.min(100, Number(instantScanState.progress || 0)));

  const trimmedAdminScanWallet = String(adminScanWalletInput || "").trim();
  const normalizedAdminScanWallet = normalizeDatasetAddress(trimmedAdminScanWallet);
  const adminScanWalletInvalid =
    isAdminInstantScan && Boolean(trimmedAdminScanWallet) && !normalizedAdminScanWallet;
  const activeScanWallet =
    isAdminInstantScan && normalizedAdminScanWallet
      ? normalizedAdminScanWallet
      : walletAddress;

  const handleRefresh = () => {
    if (adminScanWalletInvalid) return;
    instantScanStartedRef.current = false;
    startMobileInstantScan({
      forceRefresh: true,
      targetWallet:
        isAdminInstantScan && normalizedAdminScanWallet
          ? normalizedAdminScanWallet
          : undefined,
    });
  };

  return (
    <section className="relative overflow-hidden rounded-[32px] border border-slate-200/90 bg-white/98 p-5 shadow-[0_24px_70px_rgba(15,23,42,0.09)]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.10),transparent_34%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-200 to-transparent" />

      <div className="relative flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-blue-700">
            <Sparkles className="h-3.5 w-3.5" />
            {isEnglish ? "Instant wallet scan" : "Instant Wallet-Scan"}
          </div>
          <h2 className="mt-3 text-[27px] font-semibold leading-tight tracking-tight text-slate-950">
            Smart Scan
          </h2>
          <p className="mt-2 max-w-[23rem] text-sm leading-6 text-slate-600">
            {isEnglish
              ? "Overview of all potentially tax-relevant transactions in your connected wallet."
              : "Darstellung aller potenziell steuerrelevanten Transaktionen in deiner verbundenen Wallet."}
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={isBusy || adminScanWalletInvalid}
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-blue-100 bg-blue-50 text-blue-700 shadow-sm transition active:scale-[0.985] disabled:cursor-not-allowed disabled:opacity-60"
          aria-label={isEnglish ? "Refresh Smart Scan" : "Smart Scan aktualisieren"}
        >
          {isBusy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </button>
      </div>

      <div className="relative mt-5 rounded-[24px] border border-slate-200/90 bg-white/80 py-3 pl-4 pr-2.5 shadow-sm">
        <div className="flex items-start gap-2.5">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              {isEnglish ? "Connected wallet" : "Verbundene Wallet"}
            </div>
            <div className="mt-1 -mx-1 overflow-x-auto px-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              <div className="whitespace-nowrap font-mono text-[11px] font-semibold leading-4 tracking-tight text-slate-950">
                {activeScanWallet}
              </div>
            </div>
          </div>
          <MobileCopyAddressButton address={activeScanWallet} />
        </div>
      </div>

      {isAdminInstantScan ? (
        <div className="relative mt-3 rounded-[24px] border border-amber-200/80 bg-amber-50/65 p-3 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-700">
              {isEnglish ? "Admin test wallet" : "Admin-Test-Wallet"}
            </div>
            <div className="rounded-full border border-amber-200 bg-white/70 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
              Override
            </div>
          </div>
          <input
            type="text"
            inputMode="text"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={adminScanWalletInput}
            onChange={(event) => onAdminScanWalletInputChange?.(event.target.value)}
            placeholder="0x..."
            className={[
              "mt-2 w-full rounded-2xl border bg-white/90 px-3 py-2.5 font-mono text-[12px] font-semibold text-slate-950 outline-none transition",
              adminScanWalletInvalid
                ? "border-rose-200 ring-2 ring-rose-100"
                : "border-amber-100 focus:border-blue-200 focus:ring-2 focus:ring-blue-100",
            ].join(" ")}
          />
          <p
            className={[
              "mt-2 text-[11px] leading-4",
              adminScanWalletInvalid ? "text-rose-600" : "text-amber-700",
            ].join(" ")}
          >
            {adminScanWalletInvalid
              ? isEnglish
                ? "Please enter a valid 0x wallet address."
                : "Bitte eine gültige 0x-Wallet-Adresse einfügen."
              : isEnglish
                ? "Leave empty to scan the connected wallet. Refresh scans the override wallet."
                : "Leer lassen = verbundene Wallet. Aktualisieren scannt die Override-Wallet."}
          </p>
        </div>
      ) : null}

      <div className="relative mt-5">
        {instantScanState.status === "loading" ? (
          <div className="rounded-[26px] border border-blue-100 bg-blue-50/70 px-4 py-4">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              <div>
                <h3 className="text-sm font-semibold text-slate-900">
                  {isEnglish ? "Loading saved scan" : "Gespeicherter Scan wird geladen"}
                </h3>
                <p className="mt-1 text-xs leading-5 text-slate-600">
                  {isEnglish
                    ? "We load the latest snapshot for this wallet first."
                    : "Wir laden zuerst den letzten Snapshot für diese Wallet."}
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {instantScanState.status === "running" ? (
          <div className="rounded-[26px] border border-blue-100 bg-blue-50/70 px-4 py-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">
                  {instantScanState.message ||
                    (isEnglish ? "Scanning connected wallet" : "Verbundene Wallet wird gescannt")}
                </h3>
                {instantScanState.detail ? (
                  <p className="mt-1 text-xs leading-5 text-slate-600">{instantScanState.detail}</p>
                ) : null}
              </div>
              <div className="text-sm font-semibold tabular-nums text-blue-700">
                {Math.round(progress)}%
              </div>
            </div>
            <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-white">
              <div
                className="h-full rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        ) : null}

        {instantScanState.status === "ready" && instantScanState.result ? (
          <div className="space-y-4">
            <MobileInstantScanResultCard language={language} result={instantScanState.result} />

            <MobileDesktopHandoffCard
              language={language}
              copied={desktopLinkCopied}
              sharing={desktopLinkSharing}
              context="smart_scan_result"
              onShareDesktopLink={() => onShareDesktopLink?.("smart_scan_result")}
              onCopyDesktopLink={() => onCopyDesktopLink?.("smart_scan_result")}
              onView={onDesktopHandoffView}
            />
          </div>
        ) : null}

        {instantScanState.status === "error" ? (
          <div className="rounded-[26px] border border-rose-200 bg-rose-50/70 px-4 py-4">
            <h3 className="text-sm font-semibold text-slate-950">
              {isEnglish
                ? "Smart Scan could not be loaded"
                : "Smart Scan konnte nicht geladen werden"}
            </h3>
            <p className="mt-1 text-xs leading-5 text-slate-600">
              {instantScanState.error?.message ||
                (isEnglish ? "Please try again." : "Bitte versuche es erneut.")}
            </p>
            <button
              type="button"
              onClick={handleRefresh}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-rose-700 shadow-sm transition active:scale-[0.985]"
            >
              <RefreshCw className="h-4 w-4" />
              {isEnglish ? "Try again" : "Erneut versuchen"}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function MobileWalletSetCarousel({
  language,
  datasets,
  exchangeCoverageByDataset,
  openDatasetDetail,
}) {
  const datasetCount = Array.isArray(datasets) ? datasets.length : 0;
  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartXRef = useRef(null);

  const datasetIdsKey = useMemo(
    () =>
      Array.isArray(datasets) ? datasets.map((dataset) => String(dataset.id)).join("|") : "",
    [datasets]
  );

  useEffect(() => {
    setActiveIndex(0);
  }, [datasetIdsKey]);

  useEffect(() => {
    setActiveIndex((prev) => Math.min(prev, Math.max(datasetCount - 1, 0)));
  }, [datasetCount]);

  if (!datasetCount) return null;

  const safeIndex = Math.min(activeIndex, datasetCount - 1);
  const activeDataset = datasets[safeIndex];
  const hasMultiple = datasetCount > 1;
  const canGoPrev = hasMultiple && safeIndex > 0;
  const canGoNext = hasMultiple && safeIndex < datasetCount - 1;

  const goPrev = () => {
    setActiveIndex((prev) => Math.max(prev - 1, 0));
  };

  const goNext = () => {
    setActiveIndex((prev) => Math.min(prev + 1, datasetCount - 1));
  };

  const handleTouchStart = (event) => {
    touchStartXRef.current = event.touches?.[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event) => {
    const startX = touchStartXRef.current;
    touchStartXRef.current = null;
    if (startX == null) return;
    const endX = event.changedTouches?.[0]?.clientX ?? startX;
    const diff = endX - startX;
    if (Math.abs(diff) < 42) return;
    if (diff < 0 && canGoNext) goNext();
    if (diff > 0 && canGoPrev) goPrev();
  };

  return (
    <div className="relative">
      {hasMultiple ? (
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="inline-flex min-h-[30px] items-center rounded-full border border-slate-200 bg-white/82 px-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 shadow-sm">
            {language === "en"
              ? `Wallet set ${safeIndex + 1} of ${datasetCount}`
              : `Wallet-Set ${safeIndex + 1} von ${datasetCount}`}
          </div>
          <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white/88 p-1 shadow-sm">
            <button
              type="button"
              onClick={goPrev}
              disabled={!canGoPrev}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-600 transition active:scale-[0.96] disabled:text-slate-300"
              aria-label={
                language === "en" ? "Previous wallet set" : "Vorheriges Wallet-Set"
              }
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="min-w-[44px] text-center text-xs font-semibold tabular-nums text-slate-600">
              {safeIndex + 1}/{datasetCount}
            </div>
            <button
              type="button"
              onClick={goNext}
              disabled={!canGoNext}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-blue-700 transition active:scale-[0.96] disabled:text-slate-300"
              aria-label={
                language === "en" ? "Next wallet set" : "Nächstes Wallet-Set"
              }
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : null}

      <div
        className="relative touch-pan-y"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <MobileWalletSetCard
          key={activeDataset.id}
          dataset={activeDataset}
          language={language}
          coverage={exchangeCoverageByDataset[String(activeDataset.id)]}
          onOpen={openDatasetDetail}
        />
      </div>
    </div>
  );
}

function MobileWalletSetDashboardPanel({
  language,
  datasetIndex,
  walletSetYears,
  selectedWalletSetYear,
  setSelectedWalletSetYear,
  datasetsForSelectedYear,
  exchangeCoverageByDataset,
  openDatasetDetail,
  desktopLinkCopied = false,
  desktopLinkSharing = false,
  onCopyDesktopLink,
  onShareDesktopLink,
  onDesktopHandoffView,
}) {
  const hasWalletSets = Array.isArray(datasetIndex) && datasetIndex.length > 0;

  return (
    <section className="relative overflow-hidden rounded-[32px] border border-slate-200/90 bg-white/98 p-5 shadow-[0_24px_70px_rgba(15,23,42,0.09)]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.08),transparent_34%)]" />
      <div className="relative">
        <div>
          <div className="min-w-0 flex-1">
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-blue-700">
              <ShieldCheck className="h-3.5 w-3.5" />
              {language === "en" ? "Wallet Set Dashboard" : "Wallet-Set Dashboard"}
            </div>
            <h2 className="mt-3 text-[25px] font-semibold leading-tight tracking-tight text-slate-950">
              {language === "en"
                ? "Complete tax calculation"
                : "Vollständige Steuerauswertung"}
            </h2>
            <p className="mt-2 max-w-[23rem] text-sm leading-6 text-slate-600">
              {hasWalletSets
                ? language === "en"
                  ? "Your Wallet Sets combine main wallets, VeDelegate wallets, exchanges and additional addresses for the full calculation."
                  : "Deine Wallet-Sets bündeln Main Wallets, VeDelegate Wallets, Börsen und weitere Adressen für die vollständige Berechnung."
                : language === "en"
                  ? "Create a Wallet Set on a PC, Mac, or laptop to include main wallets, VeDelegate wallets, exchanges and additional addresses."
                  : "Erstelle das Wallet-Set auf einem PC oder Laptop, um Main Wallets, VeDelegate Wallets, Börsen und weitere Adressen einzubeziehen."}
            </p>
          </div>
        </div>

        {hasWalletSets ? (
          <>
            <div className="mt-5">
              <MobileYearTabs
                years={walletSetYears}
                selectedYear={selectedWalletSetYear}
                onChange={setSelectedWalletSetYear}
              />
            </div>
            <div className="mt-5">
              {datasetsForSelectedYear.length ? (
                <MobileWalletSetCarousel
                  language={language}
                  datasets={datasetsForSelectedYear}
                  exchangeCoverageByDataset={exchangeCoverageByDataset}
                  openDatasetDetail={openDatasetDetail}
                />
              ) : (
                <div className="rounded-[26px] border border-slate-200 bg-white/76 px-4 py-5 text-center shadow-sm">
                  <div className="mx-auto inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                    {selectedWalletSetYear}
                  </div>
                  <h3 className="mt-4 text-xl font-semibold tracking-tight text-slate-950">
                    {language === "en"
                      ? "No Wallet Set for this year"
                      : "Kein Wallet-Set für dieses Jahr"}
                  </h3>
                  <p className="mx-auto mt-2 max-w-[300px] text-sm leading-6 text-slate-600">
                    {language === "en"
                      ? "Create or unlock this tax year on a computer. It will appear here automatically afterwards."
                      : "Erstelle oder aktiviere dieses Steuerjahr auf einem PC oder Laptop. Danach erscheint es automatisch hier."}
                  </p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="mt-5 space-y-4">
            <div className="rounded-[26px] border border-dashed border-slate-200 bg-white/76 px-4 py-5 text-sm leading-6 text-slate-600 shadow-sm">
              {language === "en"
                ? "No Wallet Set exists yet for this account. Smart Scan still shows the mobile preview of the connected wallet activity."
                : "Für diesen Account existiert noch kein Wallet-Set. Der Smart Scan zeigt trotzdem die mobile Vorschau der verbundenen Wallet-Aktivität."}
            </div>

            <MobileDesktopHandoffCard
              language={language}
              copied={desktopLinkCopied}
              sharing={desktopLinkSharing}
              context="wallet_set_empty"
              onShareDesktopLink={() => onShareDesktopLink?.("wallet_set_empty")}
              onCopyDesktopLink={() => onCopyDesktopLink?.("wallet_set_empty")}
              onView={onDesktopHandoffView}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function DashboardCalculationPlaceholder({ language }) {
  return (
    <section className="rounded-[28px] border border-slate-200 bg-white/95 p-5 shadow-[0_18px_48px_rgba(15,23,42,0.08)]">
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
        {language === "en" ? "Dashboard update" : "Dashboard-Update"}
      </div>

      <h3 className="mt-2 text-xl font-semibold tracking-tight text-slate-950">
        {language === "en" ? "Refreshing calculation results" : "Berechnungsergebnisse werden aktualisiert"}
      </h3>

      <p className="mt-2 text-sm leading-relaxed text-slate-600">
        {language === "en"
          ? "The tax calculation is being refreshed. Updated values will appear here automatically."
          : "Die Steuerberechnung wird aktualisiert. Aktualisierte Werte erscheinen hier automatisch."}
      </p>

      <div className="mt-5 space-y-3">
        <div className="h-24 animate-pulse rounded-2xl bg-slate-100" />
        <div className="h-24 animate-pulse rounded-2xl bg-slate-100" />
        <div className="h-40 animate-pulse rounded-2xl bg-slate-100" />
      </div>
    </section>
  );
}

function MobileDetailTabs({ language, activeTab, onChange }) {
  const tabs = [
    { id: "overview", label: language === "en" ? "Overview" : "Übersicht" },
    { id: "results", label: language === "en" ? "Results" : "Ergebnis" },
    { id: "wallets", label: language === "en" ? "Wallets" : "Wallets" },
    { id: "data", label: language === "en" ? "Data" : "Daten" },
  ];

  return (
    <div className="sticky top-[88px] z-[90] -mx-1 rounded-[22px] border border-slate-200/90 bg-white/88 p-1 shadow-[0_14px_34px_rgba(15,23,42,0.08)] backdrop-blur-xl">
      <div className="grid grid-cols-4 gap-1">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onChange(tab.id)}
              className={`rounded-[18px] px-2 py-2.5 text-[12px] font-semibold transition ${
                isActive
                  ? "bg-slate-950 text-white shadow-[0_10px_24px_rgba(15,23,42,0.16)]"
                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MobileMetricTile({ label, value, hint }) {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-slate-50/75 px-4 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className="mt-1 text-[15px] font-semibold leading-snug text-slate-950">{value}</div>
      {hint ? <div className="mt-0.5 text-xs text-slate-500">{hint}</div> : null}
    </div>
  );
}

function MobileOverviewPanel({ dataset, exchangeCoverage, language, calculationState, onStartCalculation }) {
  const usage = getUsagePercent(dataset);
  const snapshotExists = getSnapshotExists(dataset);
  const txCount = Number(dataset.total_transactions || 0);
  const planLimit = getDatasetPlanLimit(dataset);

  return (
    <section className="rounded-[28px] border border-slate-200 bg-white/95 p-5 shadow-[0_14px_38px_rgba(15,23,42,0.06)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            {language === "en" ? "Snapshot control" : "Snapshot Control"}
          </div>
          <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-950">
            {language === "en" ? "Tax snapshot" : "Steuer-Snapshot"}
          </h2>
        </div>
        <div
          className={`shrink-0 rounded-full border px-3 py-1.5 text-[11px] font-semibold ${
            snapshotExists
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-amber-200 bg-amber-50 text-amber-700"
          }`}
        >
          {snapshotExists
            ? language === "en"
              ? "Available"
              : "Verfügbar"
            : language === "en"
              ? "Missing"
              : "Fehlt"}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <MobileMetricTile
          label={language === "en" ? "Tax rules" : "Steuerlogik"}
          value={dataset.residence_country || "DE"}
        />
        <MobileMetricTile
          label={language === "en" ? "Transactions" : "Transaktionen"}
          value={formatCompactNumber(txCount)}
        />
        <MobileMetricTile
          label={language === "en" ? "Last run" : "Letzter Lauf"}
          value={formatLastUpdated(dataset.last_calculated_at || dataset.lastCalculatedAt, language) || "—"}
        />
        <MobileMetricTile
          label={language === "en" ? "Created" : "Erstellt"}
          value={formatCreatedAt(dataset.purchaseDate || dataset.purchase_date, language) || "—"}
        />
      </div>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50/75 px-4 py-3">
        <div className="flex items-center justify-between text-xs font-medium text-slate-500">
          <span>{language === "en" ? "Plan usage" : "Plan-Auslastung"}</span>
          <span>
            {planLimit != null
              ? `${formatCompactNumber(txCount)} / ${formatCompactNumber(planLimit)} · ${usage}%`
              : `${formatCompactNumber(txCount)} — ${formatDatasetPlanLimitLabel(dataset, language)}`}
          </span>
        </div>
        {planLimit != null && planLimit > 0 ? (
          <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-2.5 rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500"
              style={{ width: `${Math.min(usage, 100)}%` }}
            />
          </div>
        ) : null}
      </div>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-white px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              {language === "en" ? "Data mode" : "Datenmodus"}
            </div>
            <div className="mt-1 text-sm font-semibold text-slate-950">
              {exchangeCoverage?.hasCsv
                ? language === "en"
                  ? "On-chain + exchange CSV"
                  : "On-Chain + Exchange-CSV"
                : language === "en"
                  ? "On-chain only"
                  : "Nur On-Chain"}
            </div>
            <div className="mt-1 text-xs leading-relaxed text-slate-500">
              {exchangeCoverage?.hasCsv
                ? language === "en"
                  ? `${formatCompactNumber(exchangeCoverage.rowCount)} CSV rows · ${exchangeCoverage.sourceCount} source${exchangeCoverage.sourceCount === 1 ? "" : "s"}`
                  : `${formatCompactNumber(exchangeCoverage.rowCount)} CSV-Zeilen · ${exchangeCoverage.sourceCount} Quelle${exchangeCoverage.sourceCount === 1 ? "" : "n"}`
                : language === "en"
                  ? "Add exchange CSVs in the computer browser for complete exchange coverage."
                  : "Exchange-CSVs fügst du für vollständige Börsendaten am PC oder Laptop hinzu."}
            </div>
          </div>
          <ShieldCheck className="mt-1 h-5 w-5 shrink-0 text-blue-600" />
        </div>
      </div>

      <button
        type="button"
        onClick={onStartCalculation}
        disabled={calculationState.isRunning}
        className="mt-5 inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 text-[15px] font-semibold text-white shadow-[0_14px_30px_rgba(37,99,235,0.26)] transition active:scale-[0.985] disabled:opacity-60"
      >
        {calculationState.isRunning ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {language === "en" ? "Calculation running…" : "Berechnung läuft…"}
          </>
        ) : (
          <>
            <RefreshCw className="h-4 w-4" />
            {snapshotExists
              ? language === "en"
                ? "Recalculate snapshot"
                : "Snapshot neu berechnen"
              : language === "en"
                ? "Start analysis"
                : "Analyse starten"}
          </>
        )}
      </button>
    </section>
  );
}

function MobileWalletAddressRow({ label, address, txCount, language, copiedAddress, onCopy }) {
  const explorerUrl = getVeChainExplorerAddressUrl(address);
  const isCopied = Boolean(address && copiedAddress === address);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
          <div className="mt-1 font-mono text-[13px] font-semibold text-slate-950">{formatShortAddress(address)}</div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={() => onCopy(address)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-600 active:scale-[0.96]"
            aria-label={language === "en" ? "Copy address" : "Adresse kopieren"}
          >
            {isCopied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
          </button>
          {explorerUrl ? (
            <a
              href={explorerUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-600 active:scale-[0.96]"
              aria-label={language === "en" ? "Open explorer" : "Im Explorer öffnen"}
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          ) : null}
        </div>
      </div>

      <div className="mt-3 inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
        {language === "en" ? "Transactions" : "Transaktionen"}: {formatCompactNumber(txCount)}
      </div>
    </div>
  );
}

function MobileWalletsPanel({ dataset, language, copiedAddress, onCopyAddress }) {
  const wallets = getWalletPanelPairRows(dataset);

  return (
    <section className="rounded-[28px] border border-slate-200 bg-white/95 p-5 shadow-[0_14px_38px_rgba(15,23,42,0.06)]">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            {language === "en" ? "Wallets in set" : "Wallets im Set"}
          </div>
          <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-950">
            {wallets.length} {language === "en" ? "entries" : "Einträge"}
          </h2>
        </div>
        <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600">
          {language === "en" ? "Read-only" : "Read-only"}
        </div>
      </div>

      <div className="mt-5 space-y-4">
        {wallets.map((wallet, index) => (
          <div
            key={wallet.wallet_pair_id || `${wallet.main_wallet}-${wallet.delegate_wallet || ""}-${index}`}
            className="rounded-[24px] border border-slate-200 bg-slate-50/60 p-3"
          >
            <div className="mb-3 flex items-center justify-between gap-3 px-1">
              <div className="text-xs font-semibold text-slate-700">
                {language === "en" ? "Wallet pair" : "Wallet-Paar"} #{index + 1}
              </div>
              <div className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                {language === "en" ? "Total" : "Gesamt"} {formatCompactNumber(wallet.total_tx_count || 0)}
              </div>
            </div>

            <div className="space-y-3">
              <MobileWalletAddressRow
                label={language === "en" ? "Main wallet" : "Main-Wallet"}
                address={wallet.main_wallet}
                txCount={wallet.main_tx_count || 0}
                language={language}
                copiedAddress={copiedAddress}
                onCopy={onCopyAddress}
              />

              {wallet.delegate_wallet ? (
                <MobileWalletAddressRow
                  label={language === "en" ? "VeDelegate wallet" : "VeDelegate-Wallet"}
                  address={wallet.delegate_wallet}
                  txCount={wallet.delegate_tx_count || 0}
                  language={language}
                  copiedAddress={copiedAddress}
                  onCopy={onCopyAddress}
                />
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function MobileDataPanel({ exchangeCoverage, language }) {
  const hasCsv = Boolean(exchangeCoverage?.hasCsv);

  return (
    <section className="rounded-[28px] border border-slate-200 bg-white/95 p-5 shadow-[0_14px_38px_rgba(15,23,42,0.06)]">
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
        {language === "en" ? "Data coverage" : "Datenabdeckung"}
      </div>
      <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-950">
        {hasCsv
          ? language === "en"
            ? "On-chain + exchange CSV"
            : "On-Chain + Exchange-CSV"
          : language === "en"
            ? "On-chain only"
            : "Nur On-Chain"}
      </h2>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <MobileMetricTile
          label={language === "en" ? "CSV rows" : "CSV-Zeilen"}
          value={hasCsv ? formatCompactNumber(exchangeCoverage.rowCount) : "—"}
        />
        <MobileMetricTile
          label={language === "en" ? "Sources" : "Quellen"}
          value={hasCsv ? exchangeCoverage.sourceCount : "—"}
        />
        <div className="col-span-2">
          <MobileMetricTile
            label={language === "en" ? "Last import" : "Letzter Import"}
            value={hasCsv ? formatCompactDateTime(exchangeCoverage.latestImport, language) : "—"}
            hint={
              hasCsv
                ? language === "en"
                  ? "Included in recalculations."
                  : "Wird bei Neuberechnungen berücksichtigt."
                : language === "en"
                  ? "CSV management is available on a computer."
                  : "CSV-Verwaltung ist auf einem PC oder Laptop verfügbar."
            }
          />
        </div>
      </div>
    </section>
  );
}

function MobileStatePanel({
  language,
  eyebrow,
  title,
  description,
  note,
  noteLabel,
  ctaLabel,
  ctaIcon: CtaIcon = null,
  onCta,
}) {
  const resolvedNoteLabel =
    noteLabel || (language === "en" ? "Note" : "Hinweis");
  const trustChips =
    language === "en"
      ? ["Read-only", "Snapshots", "Computer setup"]
      : ["Read-only", "Snapshots", "Einrichtung am Computer"];

  return (
    <div className="flex min-h-[calc(100vh-260px)] items-start pt-3">
      <section className="relative w-full overflow-hidden rounded-[32px] border border-slate-200/90 bg-white/95 px-6 py-7 shadow-[0_18px_54px_rgba(15,23,42,0.07)]">
        <div className="pointer-events-none absolute -right-20 -top-20 h-52 w-52 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-52 w-52 rounded-full bg-emerald-400/10 blur-3xl" />

        <div className="relative mx-auto max-w-[360px] text-center">
          {eyebrow ? (
            <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-blue-200/70 bg-blue-50/80 px-3 py-1.5 shadow-sm">
              <span className="h-2 w-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-500" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-blue-700">
                {eyebrow}
              </span>
            </div>
          ) : null}

          <h1 className="mx-auto mt-4 max-w-[340px] text-[34px] font-semibold leading-[1.02] tracking-tight text-slate-950">
            {title}
          </h1>

          <p className="mx-auto mt-4 max-w-[330px] text-[15px] leading-7 text-slate-600">
            {description}
          </p>

          <div className="mx-auto mt-5 flex max-w-[330px] flex-wrap items-center justify-center gap-2">
            {trustChips.map((chip) => (
              <span
                key={chip}
                className="rounded-full border border-slate-200 bg-slate-50/90 px-3 py-1.5 text-xs font-semibold text-slate-600"
              >
                {chip}
              </span>
            ))}
          </div>

          {note ? (
            <div className="mt-6 rounded-[22px] border border-slate-200 bg-slate-50/75 px-4 py-4 text-left">
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                {resolvedNoteLabel}
              </div>
              <p className="mt-2 text-sm leading-6 text-slate-600">{note}</p>
            </div>
          ) : null}

          {ctaLabel && onCta ? (
            <button
              type="button"
              onClick={onCta}
              className="mt-7 inline-flex min-h-[56px] w-full items-center justify-center gap-2.5 rounded-[20px] bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-[17px] font-semibold text-white shadow-[0_16px_34px_rgba(59,130,246,0.30)] transition active:scale-[0.985]"
            >
              {CtaIcon ? <CtaIcon className="h-[18px] w-[18px]" /> : null}
              <span>{ctaLabel}</span>
            </button>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function MobileFooter({ language, onOpenLegal, pushBelowFold = false }) {
  const hasTelegram = Boolean(MOBILE_TELEGRAM_URL);
  const hasX = Boolean(MOBILE_X_URL);

  return (
    <footer
      className={[
        pushBelowFold ? "mt-12" : "mt-8",
        "pb-8 text-center",
      ].join(" ")}
    >
      {(hasTelegram || hasX) ? (
        <div className="mx-auto mb-6 max-w-[360px] rounded-[24px] border border-slate-200 bg-white/88 px-4 py-4 shadow-[0_12px_34px_rgba(15,23,42,0.05)]">
          <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
            Community
          </div>

          <div className="grid grid-cols-2 gap-3">
            {hasTelegram ? (
              <a
                href={MOBILE_TELEGRAM_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-[0_10px_22px_rgba(37,99,235,0.20)] transition active:scale-[0.985]"
              >
                <Send className="h-4 w-4" />
                <span>Telegram</span>
              </a>
            ) : null}

            {hasX ? (
              <a
                href={MOBILE_X_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white shadow-[0_10px_22px_rgba(15,23,42,0.18)] transition active:scale-[0.985]"
              >
                <span className="text-base leading-none">𝕏</span>
                <span>{language === "en" ? "Follow" : "Folgen"}</span>
              </a>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="mx-auto flex max-w-[360px] flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm text-slate-500">
        <span>{COPYRIGHT_LABEL}</span>
        <span className="text-slate-300">•</span>
        <button
          type="button"
          onClick={onOpenLegal}
          className="font-medium text-slate-600 underline-offset-4 hover:underline"
        >
          {language === "en" ? "Legal Notice" : "Impressum"}
        </button>
        <span className="text-slate-300">•</span>
        <span className="rounded-full border border-slate-200 bg-white px-3 py-1 font-semibold text-slate-600">
          {APP_VERSION_LABEL}
        </span>
      </div>

      <div className="mx-auto mt-5 max-w-[390px]">
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
          {language === "en" ? "Data sources" : "Datenquellen"}
        </div>
        <div className="flex justify-center">
          <div className="inline-flex min-h-[44px] max-w-full flex-wrap items-center justify-center gap-2.5 rounded-full border border-slate-200 bg-white/95 px-4 py-2 text-xs shadow-[0_10px_28px_rgba(15,23,42,0.05)]">
            <span className="font-semibold text-slate-500">Powered by</span>

            <a
              href={MOBILE_COINGECKO_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center rounded-full px-1 py-0.5 transition hover:bg-slate-50"
              aria-label="CoinGecko API"
            >
              <img
                src={MOBILE_COINGECKO_LOGO_SRC}
                alt="CoinGecko API"
                className="h-5 max-w-[140px] object-contain"
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
            </a>

            <span
              className="h-1 w-1 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 shadow-[0_0_0_3px_rgba(99,102,241,0.12)]"
              aria-hidden="true"
            />

            <a
              href={MOBILE_VECHAINSTATS_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full px-1 py-0.5 transition hover:bg-slate-50"
              aria-label="vechainstats API"
            >
              <img
                src={MOBILE_VECHAINSTATS_LOGO_SRC}
                alt="vechainstats"
                className="h-5 w-5 rounded-[5px] object-cover"
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
              <span className="font-semibold text-slate-700">vechainstats</span>
              <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                API
              </span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function MobileNotice({ notice, onDismiss }) {
  if (!notice?.message) return null;

  const tone =
    notice.type === "error"
      ? "border-red-200 bg-red-50 text-red-800"
      : notice.type === "warning"
        ? "border-amber-200 bg-amber-50 text-amber-800"
        : "border-slate-200 bg-white/95 text-slate-800";

  const dot =
    notice.type === "error"
      ? "bg-red-500"
      : notice.type === "warning"
        ? "bg-amber-500"
        : "bg-emerald-500";

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[500] px-4 pb-[max(env(safe-area-inset-bottom),16px)]">
      <div
        className={`pointer-events-auto mx-auto flex max-w-[390px] items-start gap-3 rounded-[22px] border px-4 py-3 text-sm shadow-[0_18px_50px_rgba(15,23,42,0.18)] backdrop-blur-md ${tone}`}
      >
        <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${dot}`} />
        <div className="min-w-0 flex-1 leading-5">{notice.message}</div>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 rounded-full px-2 text-base leading-none opacity-60 transition hover:opacity-100"
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}

export default function VeWorldMobileApp() {
  const { language, changeLanguage } = useLanguage();
  const {
    user,
    isAuthenticated,
    isLoading: authLoading,
    logout,
    login,
    checkAuthStatus,
    setSource: setAuthSource,
  } = useAuth();
  const {
    account,
    setSource,
    connect,
    disconnect: disconnectWalletKit,
  } = useWallet();

  const [showWalletModal, setShowWalletModal] = useState(false);
  const [forceFreshVeWorldLogin, setForceFreshVeWorldLogin] = useState(false);
  const [resetQrCode, setResetQrCode] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const [showLegalModal, setShowLegalModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showFunctionModal, setShowFunctionModal] = useState(false);
  const [showRoadmap, setShowRoadmap] = useState(false);

  const [summaryState, setSummaryState] = useState(null);
  const [datasetIndex, setDatasetIndex] = useState([]);
  const [mobileScreen, setMobileScreen] = useState("list"); // list | detail
  const [detailDatasetId, setDetailDatasetId] = useState(null);
  const [selectedWalletSetYear, setSelectedWalletSetYear] = useState(
    new Date().getFullYear()
  );
  const [exchangeCoverageByDataset, setExchangeCoverageByDataset] = useState({});
  const [copiedAddress, setCopiedAddress] = useState(null);
  const [desktopLinkCopied, setDesktopLinkCopied] = useState(false);
  const [desktopLinkSharing, setDesktopLinkSharing] = useState(false);
  const [uiState, setUiState] = useState("idle"); // idle | loading | ready | empty | error
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [exchangeCoverage, setExchangeCoverage] = useState(null);
  const [calculationState, setCalculationState] = useState(INITIAL_CALCULATION_STATE);
  const [instantScanState, setInstantScanState] = useState(INITIAL_INSTANT_SCAN_STATE);
  const [adminInstantScanWalletInput, setAdminInstantScanWalletInput] = useState("");
  const instantScanStartedRef = useRef(false);
  const [mobileNotice, setMobileNotice] = useState(null);
  const mobileNoticeTimerRef = useRef(null);
  const embeddedLoginInFlightRef = useRef(false);
  /** Nur true während `startEmbeddedVeWorldLogin` — verhindert Race mit WalletConnectModal / Account-Updates. */
  const embeddedVeWorldFlowRef = useRef(false);
  const embeddedConnectPromiseRef = useRef(null);
  const embeddedLoginAttemptRef = useRef(0);
  const snapshotSummaryCacheRef = useRef(new Map());
  const desktopLinkShareInFlightRef = useRef(false);
  const mobileComputerHandoffViewsRef = useRef(new Set());

  const loggedInMobileWallet = String(user?.walletAddress || "").toLowerCase().trim();
  const isMobileInstantScanAdmin =
    MOBILE_INSTANT_SCAN_ADMIN_WALLETS.has(loggedInMobileWallet);
  const normalizedAdminInstantScanWallet = normalizeDatasetAddress(
    adminInstantScanWalletInput
  );
  const effectiveMobileInstantScanWallet =
    isMobileInstantScanAdmin && normalizedAdminInstantScanWallet
      ? normalizedAdminInstantScanWallet
      : loggedInMobileWallet;

  const resetMobileWalletUiState = useCallback(() => {
    setSummaryState(null);
    setDatasetIndex([]);
    setMobileScreen("list");
    setDetailDatasetId(null);
    setSelectedWalletSetYear(new Date().getFullYear());
    setExchangeCoverageByDataset({});
    setExchangeCoverage(null);
    snapshotSummaryCacheRef.current.clear();
    setCalculationState(INITIAL_CALCULATION_STATE);
    setInstantScanState(INITIAL_INSTANT_SCAN_STATE);
    setAdminInstantScanWalletInput("");
    instantScanStartedRef.current = false;
    setIsRefreshing(false);
    setUiState("idle");
  }, []);

  const showMobileNotice = useCallback((message, type = "info", duration = 2600) => {
    if (!message) return;
    if (mobileNoticeTimerRef.current) {
      window.clearTimeout(mobileNoticeTimerRef.current);
    }
    setMobileNotice({
      id: Date.now(),
      message,
      type,
    });
    mobileNoticeTimerRef.current = window.setTimeout(() => {
      setMobileNotice(null);
      mobileNoticeTimerRef.current = null;
    }, duration);
  }, []);

  const dismissMobileNotice = useCallback(() => {
    if (mobileNoticeTimerRef.current) {
      window.clearTimeout(mobileNoticeTimerRef.current);
      mobileNoticeTimerRef.current = null;
    }
    setMobileNotice(null);
  }, []);

  const handleCopyAddress = useCallback(
    async (address) => {
      if (!address) return;
      try {
        await navigator.clipboard.writeText(address);
        setCopiedAddress(address);
        showMobileNotice(
          language === "en" ? "Address copied." : "Adresse kopiert.",
          "success",
          1600
        );
        window.setTimeout(() => setCopiedAddress(null), 1200);
      } catch {
        showMobileNotice(
          language === "en" ? "Copy failed." : "Kopieren fehlgeschlagen.",
          "error",
          2200
        );
      }
    },
    [language, showMobileNotice]
  );

  const handleCopyDesktopLink = useCallback(
    async (context = "mobile") => {
      const ok = await copyTextToClipboard(V3TAX_DESKTOP_URL);

      if (ok) {
        setDesktopLinkCopied(true);

        showMobileNotice(
          language === "en"
            ? "Link copied. Open v3tax.app on your computer and connect the same VeWorld wallet."
            : "Link kopiert. Öffne v3tax.app auf deinem PC oder Laptop und verbinde dort dieselbe VeWorld-Wallet.",
          "success",
          3600
        );

        window.setTimeout(() => setDesktopLinkCopied(false), 1600);
      } else {
        showMobileNotice(
          language === "en" ? "Copy failed." : "Kopieren fehlgeschlagen.",
          "error",
          2200
        );
      }

      const trackingPayload = {
        source: "mobile",
        context,
        success: ok,
        walletAddress: user?.walletAddress || null,
        hasWalletSets: datasetIndex.length > 0,
      };

      void trackPlatformActivity("mobile_computer_link_copy", trackingPayload);
      void trackPlatformActivity("mobile_desktop_link_copy", trackingPayload);
    },
    [datasetIndex.length, language, showMobileNotice, user?.walletAddress]
  );

  const handleShareDesktopLink = useCallback(
    async (context = "mobile") => {
      if (typeof navigator === "undefined" || typeof navigator.share !== "function") {
        return handleCopyDesktopLink(context);
      }

      if (desktopLinkShareInFlightRef.current) return;
      desktopLinkShareInFlightRef.current = true;
      setDesktopLinkSharing(true);

      let success = false;
      let cancelled = false;

      try {
        await navigator.share({
          title: "V3Tax",
          text:
            language === "en"
              ? "Open V3Tax on your PC, Mac, or laptop and connect the same VeWorld wallet to start the full tax calculation."
              : "Öffne V3Tax auf deinem PC oder Laptop und verbinde dort dieselbe VeWorld-Wallet, um die vollständige Steuerberechnung zu starten.",
          url: V3TAX_DESKTOP_URL,
        });
        success = true;
        showMobileNotice(
          language === "en"
            ? "Link shared. Open it on your computer to continue."
            : "Link geteilt. Öffne ihn anschließend auf deinem PC oder Laptop.",
          "success",
          3200
        );
      } catch (error) {
        cancelled = error?.name === "AbortError";
        if (!cancelled) {
          showMobileNotice(
            language === "en"
              ? "The link could not be shared. You can copy it instead."
              : "Link konnte nicht geteilt werden. Du kannst ihn stattdessen kopieren.",
            "error",
            3200
          );
        }
      } finally {
        void trackPlatformActivity("mobile_computer_link_share", {
          source: "mobile",
          context,
          success,
          cancelled,
          shareSupported: true,
          walletAddress: user?.walletAddress || null,
          hasWalletSets: datasetIndex.length > 0,
        });
        desktopLinkShareInFlightRef.current = false;
        setDesktopLinkSharing(false);
      }
    },
    [datasetIndex.length, handleCopyDesktopLink, language, showMobileNotice, user?.walletAddress]
  );

  const handleDesktopHandoffView = useCallback(
    (context = "mobile") => {
      if (mobileComputerHandoffViewsRef.current.has(context)) return;
      mobileComputerHandoffViewsRef.current.add(context);

      void trackPlatformActivity("mobile_computer_handoff_view", {
        source: "mobile",
        context,
        walletAddress: user?.walletAddress || null,
        hasWalletSets: datasetIndex.length > 0,
        shareSupported:
          typeof navigator !== "undefined" && typeof navigator.share === "function",
      });
    },
    [datasetIndex.length, user?.walletAddress]
  );

  useEffect(() => {
    return () => {
      if (mobileNoticeTimerRef.current) {
        window.clearTimeout(mobileNoticeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const unlock = () => unlockMobileWebViewInteraction();
    unlock();
    const t1 = setTimeout(unlock, 250);
    const t2 = setTimeout(unlock, 900);
    const t3 = setTimeout(unlock, 1800);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  const {
    isEmbeddedVeWorld,
    canUseDirectEmbeddedConnect,
    debug: veWorldRuntimeDebug,
  } = useMemo(
    () => getVeWorldRuntimeFlags(),
    []
  );

  useEffect(() => {
    console.info("[V3Tax Mobile] VeWorld runtime", {
      isEmbeddedVeWorld,
      canUseDirectEmbeddedConnect,
      veWorldRuntimeDebug,
    });
  }, [isEmbeddedVeWorld, canUseDirectEmbeddedConnect, veWorldRuntimeDebug]);

  const applyMobileAnalysisJobState = useCallback(
    (job) => {
      if (!job) return;
      const step = job.currentStep || "initializing";
      const nextProgress =
        typeof job.progress === "number"
          ? Math.max(0, Math.min(100, Math.round(job.progress)))
          : 0;
      setCalculationState({
        isRunning: job.status !== "done" && job.status !== "error",
        message: getMobileStepLabel(step, language),
        detail: normalizeMobileProgressDetail(job.detail || job.message || "", language),
        jobId: job.id ?? null,
        progress: nextProgress,
        currentStep: step,
        completedSteps: Array.isArray(job.completedSteps) ? job.completedSteps : [],
        indeterminate: false,
      });
    },
    [language]
  );

  const applyInstantScanJobState = useCallback(
    (job) => {
      if (!job) return;
      const step = job.currentStep || "initializing";
      const nextProgress =
        typeof job.progress === "number"
          ? Math.max(0, Math.min(100, Math.round(job.progress)))
          : 0;
      setInstantScanState((prev) => ({
        ...prev,
        status: prev.status === "ready" ? prev.status : "running",
        jobId: job.id ?? prev.jobId,
        currentStep: step,
        progress: nextProgress,
        message: getMobileStepLabel(step, language),
        detail: normalizeMobileProgressDetail(
          job.detail || job.message || "",
          language
        ),
        completedSteps: Array.isArray(job.completedSteps)
          ? job.completedSteps
          : prev.completedSteps,
      }));
    },
    [language]
  );

  const closeAllMenus = useCallback(() => {
    setShowMobileMenu(false);
    setShowUserMenu(false);
  }, []);

  /** Debug-Overlay entfernt — No-Op für bestehende Aufrufe */
  const appendMobileLog = useCallback(() => {}, []);

  useEffect(() => {
    // Legacy cleanup only: earlier builds used a full WebView reload after embedded
    // VeWorld login. That reload caused the visible 3–4s blank screen. The current
    // flow keeps React mounted and only clears stale transition flags if they exist.
    const raw = sessionStorage.getItem(MOBILE_EMBEDDED_LOGIN_RELOAD_KEY);
    if (!raw) return;
    sessionStorage.removeItem(MOBILE_EMBEDDED_LOGIN_RELOAD_KEY);
    sessionStorage.removeItem("v3tax:mobile_fresh_login");
    sessionStorage.removeItem("v3tax:force_logged_out");
    sessionStorage.removeItem("v3tax:expected_wallet_after_login");
    sessionStorage.removeItem(MOBILE_MANUAL_DISCONNECT_KEY);
    unlockMobileWebViewInteraction();
    setTimeout(unlockMobileWebViewInteraction, 250);
    appendMobileLog("embedded login legacy reload flag cleaned");
  }, [appendMobileLog]);

  const normalizeWalletAddress = useCallback((value) => {
    if (!value) return null;

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return null;
      return trimmed.includes(":")
        ? trimmed.split(":").pop()?.toLowerCase() || null
        : trimmed.toLowerCase();
    }

    if (typeof value === "object") {
      const raw =
        value.address ||
        value.walletAddress ||
        value.account ||
        value.id ||
        null;

      if (!raw || typeof raw !== "string") return null;

      const trimmed = raw.trim();
      if (!trimmed) return null;

      return trimmed.includes(":")
        ? trimmed.split(":").pop()?.toLowerCase() || null
        : trimmed.toLowerCase();
    }

    return null;
  }, []);

  const finishEmbeddedVeWorldLogin = useCallback(
    async (candidateAccount) => {
      const walletAddress =
        normalizeWalletAddress(candidateAccount) ||
        normalizeWalletAddress(account);

      if (!walletAddress) {
        throw new Error("Wallet address missing after embedded VeWorld connect");
      }

      appendMobileLog("finishEmbeddedVeWorldLogin started", {
        wallet: walletAddress,
        embedded: canUseDirectEmbeddedConnect,
      });

      try {
        sessionStorage.setItem("v3tax:expected_wallet_after_login", walletAddress);
      } catch {
        /* ignore */
      }

      const loginResult = await login({
        walletAddress,
        walletType: "veworld",
        source: "veworld",
        skipLatestPlan: true,
        latestPlanMode: "async",
      });

      if (!loginResult?.success) {
        throw new Error(loginResult?.error || "Backend login failed");
      }

      try {
        sessionStorage.removeItem("v3tax:mobile_fresh_login");
        sessionStorage.removeItem("v3tax:force_logged_out");
        sessionStorage.removeItem("v3tax:expected_wallet_after_login");
        sessionStorage.removeItem(MOBILE_MANUAL_DISCONNECT_KEY);
      } catch {
        /* ignore */
      }

      setSource("veworld");
      setAuthSource("veworld");
      closeAllMenus();

      appendMobileLog("embedded login backend auth completed", {
        wallet: walletAddress,
      });

      try {
        await withTimeout(
          checkAuthStatus({ silent: true }),
          canUseDirectEmbeddedConnect ? 3500 : 2500,
          "post-login auth status"
        );
      } catch (error) {
        appendMobileLog("post-login auth status ignored", {
          message: error?.message,
        });
      }

      unlockMobileWebViewInteraction();
      setTimeout(unlockMobileWebViewInteraction, 250);

      window.dispatchEvent(new Event("v3tax-auth"));

      appendMobileLog("finishEmbeddedVeWorldLogin completed", {
        wallet: walletAddress,
      });
    },
    [
      account,
      appendMobileLog,
      canUseDirectEmbeddedConnect,
      checkAuthStatus,
      closeAllMenus,
      login,
      normalizeWalletAddress,
      setAuthSource,
      setSource,
    ]
  );

  const startBrowserMobileLogin = useCallback(async () => {
    appendMobileLog("startBrowserMobileLogin started");

    closeAllMenus();

    sessionStorage.setItem("v3tax:force_logged_out", "1");
    sessionStorage.setItem("v3tax:mobile_fresh_login", "1");

    const disconnectResult = await disconnectEverywhere();

    if (!disconnectResult?.success) {
      appendMobileLog("disconnectEverywhere before fresh login failed", {
        error: disconnectResult?.error || "unknown disconnect error",
      });

      sessionStorage.removeItem("v3tax:force_logged_out");
      sessionStorage.removeItem("v3tax:mobile_fresh_login");

      showMobileNotice(
        language === "en"
          ? "Could not fully disconnect VeWorld. Please try again."
          : "VeWorld konnte nicht vollständig getrennt werden. Bitte erneut versuchen.",
        "error",
        4200
      );
      return;
    }

    appendMobileLog("disconnectEverywhere before fresh login done");

    try {
      await logout();
      appendMobileLog("backend logout after full disconnect done");
    } catch (error) {
      appendMobileLog("backend logout after full disconnect failed", {
        message: error?.message,
      });
    }

    try {
      setSource(null);
      setAuthSource(null);
      appendMobileLog("setSource cleanup before fresh login done");
    } catch (error) {
      appendMobileLog("setSource cleanup before fresh login failed", {
        message: error?.message,
      });
    }

    localStorage.removeItem("v3tax_auth");
    localStorage.removeItem("walletconnect");
    localStorage.removeItem("walletconnect-v2");
    localStorage.removeItem("wc_v3tax_pairing_topic");

    Object.keys(localStorage).forEach((key) => {
      if (
        key.startsWith("wc@") ||
        key.startsWith("wc_") ||
        key.includes("walletconnect")
      ) {
        localStorage.removeItem(key);
      }
    });

    appendMobileLog("all walletconnect artifacts removed before fresh login");

    setForceFreshVeWorldLogin(true);
    setResetQrCode(true);
    setShowWalletModal(true);

    setTimeout(() => {
      setResetQrCode(false);
      appendMobileLog("resetQrCode reverted after fresh login open");
    }, 250);
  }, [
    appendMobileLog,
    closeAllMenus,
    disconnectEverywhere,
    language,
    logout,
    setSource,
    setAuthSource,
    showMobileNotice,
  ]);

  const startEmbeddedVeWorldLogin = useCallback(
    async ({ forceFresh = false } = {}) => {
      appendMobileLog("startEmbeddedVeWorldLogin started", { forceFresh });

      closeAllMenus();

      const attemptId = embeddedLoginAttemptRef.current + 1;
      embeddedLoginAttemptRef.current = attemptId;
      embeddedVeWorldFlowRef.current = true;

      const clearFreshLoginState = () => {
        sessionStorage.removeItem("v3tax:mobile_fresh_login");
        sessionStorage.removeItem("v3tax:force_logged_out");
        sessionStorage.removeItem("v3tax:expected_wallet_after_login");
      };

      const completeWithAddress = async (walletAddress) => {
        if (!walletAddress || embeddedLoginAttemptRef.current !== attemptId)
          return false;
        if (embeddedLoginInFlightRef.current) return true;
        embeddedLoginInFlightRef.current = true;
        try {
          await finishEmbeddedVeWorldLogin(walletAddress);
          return true;
        } finally {
          embeddedLoginInFlightRef.current = false;
          embeddedVeWorldFlowRef.current = false;
        }
      };

      try {
        sessionStorage.removeItem(MOBILE_MANUAL_DISCONNECT_KEY);
        sessionStorage.removeItem("v3tax:force_logged_out");
        sessionStorage.setItem("v3tax:mobile_fresh_login", "1");
        // Wichtig: Beim normalen Re-Login NICHT WalletConnect/DAppKit hart trennen.
        // VeWorld liefert im embedded Browser oft sofort wieder den bestehenden Account.
        // Genau dieses harte Trennen + Reload hat den sichtbaren Blank-Screen erzeugt.
        try {
          setSource("veworld");
          setAuthSource("veworld");
        } catch {
          /* ignore */
        }

        const currentEmbeddedAccount = normalizeWalletAddress(account);
        if (!forceFresh && currentEmbeddedAccount) {
          appendMobileLog("embedded VeWorld reused injected account", {
            account: currentEmbeddedAccount,
          });
          const completed = await completeWithAddress(currentEmbeddedAccount);
          if (completed) return;
        }

        if (forceFresh) {
          appendMobileLog("embedded VeWorld force-fresh cleanup started");
          resetMobileWalletUiState();
          try {
            await withTimeout(disconnectEverywhere(), 4500, "disconnectEverywhere");
          } catch (disconnectError) {
            appendMobileLog("force-fresh disconnectEverywhere ignored", {
              message: disconnectError?.message,
            });
          }
          try {
            disconnectWalletKit?.();
          } catch (kitErr) {
            appendMobileLog("force-fresh dapp-kit disconnect ignored", {
              message: kitErr?.message,
            });
          }
          localStorage.removeItem("v3tax_auth");
          localStorage.removeItem("walletconnect");
          localStorage.removeItem("walletconnect-v2");
          localStorage.removeItem("wc_v3tax_pairing_topic");
          try {
            setSource("veworld");
            setAuthSource("veworld");
          } catch {
            /* ignore */
          }
        }

        const connectPromise = Promise.resolve(connect());
        embeddedConnectPromiseRef.current = connectPromise;
        const connectResult = await connectPromise;

        if (embeddedLoginAttemptRef.current !== attemptId) return;

        appendMobileLog("embedded VeWorld connect resolved", {
          hasResult: Boolean(connectResult),
        });

        const resolvedAddress =
          normalizeWalletAddress(connectResult?.account) ||
          normalizeWalletAddress(connectResult?.address) ||
          normalizeWalletAddress(connectResult) ||
          normalizeWalletAddress(account);

        if (resolvedAddress) {
          const completed = await completeWithAddress(resolvedAddress);
          if (completed) return;
        }

        // Fallback: manche embedded providers setzen `account` erst im nächsten React-Tick.
        // Der account-effect unten finalisiert dann den Login; bis dahin bleibt die
        // sichtbare Login-Progress-Card stehen, aber die App wird nicht entleert.
        setTimeout(() => {
          if (embeddedLoginAttemptRef.current !== attemptId) return;
          if (embeddedLoginInFlightRef.current) return;
          const stillFreshLogin =
            sessionStorage.getItem("v3tax:mobile_fresh_login") === "1";
          if (!stillFreshLogin) {
            embeddedVeWorldFlowRef.current = false;
            return;
          }
          appendMobileLog("embedded VeWorld waiting for account-effect fallback");
        }, 250);

        setTimeout(() => {
          if (embeddedLoginAttemptRef.current !== attemptId) return;
          const stillFreshLogin =
            sessionStorage.getItem("v3tax:mobile_fresh_login") === "1";
          if (stillFreshLogin && !embeddedLoginInFlightRef.current) {
            appendMobileLog("embedded VeWorld login safety timeout");
            clearFreshLoginState();
            embeddedVeWorldFlowRef.current = false;
            showMobileNotice(
              language === "en"
                ? "Login took too long. Please try again."
                : "Login hat zu lange gedauert. Bitte erneut versuchen.",
              "error",
              4200
            );
          }
        }, 20000);
      } catch (error) {
        appendMobileLog("embedded VeWorld direct connect failed", {
          message: error?.message,
        });
        clearFreshLoginState();
        embeddedVeWorldFlowRef.current = false;
        showMobileNotice(
          language === "en" ? "Login failed." : "Login fehlgeschlagen.",
          "error",
          3600
        );
      }
    },
    [
      account,
      appendMobileLog,
      closeAllMenus,
      connect,
      disconnectEverywhere,
      disconnectWalletKit,
      finishEmbeddedVeWorldLogin,
      language,
      normalizeWalletAddress,
      resetMobileWalletUiState,
      setAuthSource,
      setSource,
      showMobileNotice,
    ]
  );

  const startFreshMobileLogin = useCallback(
    async (options = {}) => {
      sessionStorage.removeItem(MOBILE_MANUAL_DISCONNECT_KEY);

      if (isEmbeddedVeWorld && canUseDirectEmbeddedConnect) {
        await startEmbeddedVeWorldLogin(options);
        return;
      }

      await startBrowserMobileLogin();
    },
    [
      canUseDirectEmbeddedConnect,
      isEmbeddedVeWorld,
      startBrowserMobileLogin,
      startEmbeddedVeWorldLogin,
    ]
  );

  useEffect(() => {
    const validateMobileSession = async () => {
      try {
        if (canUseDirectEmbeddedConnect) {
          appendMobileLog("mobile session validation skipped inside real embedded VeWorld");
          return;
        }

        const freshLoginInProgress =
          sessionStorage.getItem("v3tax:mobile_fresh_login") === "1";

        if (freshLoginInProgress) {
          appendMobileLog(
            "mobile session validation skipped because fresh login is in progress"
          );
          return;
        }

        const localAuth = localStorage.getItem("v3tax_auth");
        if (!localAuth) return;

        const sessionResult = await findExistingSession();
        const hasActiveVeWorldSession =
          !!sessionResult?.success && !!sessionResult?.session;

        appendMobileLog("mobile session validation", {
          hasLocalAuth: true,
          hasActiveVeWorldSession,
        });

        if (!hasActiveVeWorldSession) {
          appendMobileLog("stale mobile auth detected - clearing local auth");

          sessionStorage.setItem("v3tax:force_logged_out", "1");

          try {
            setSource(null);
            setAuthSource(null);
          } catch {
            /* ignore */
          }

          localStorage.removeItem("v3tax_auth");

          await logout();

          setSummaryState(null);
          setDatasetIndex([]);
          setMobileScreen("list");
          setDetailDatasetId(null);
          setExchangeCoverage(null);
          setCalculationState(INITIAL_CALCULATION_STATE);
          setIsRefreshing(false);
          setUiState("idle");
        }
      } catch (error) {
        appendMobileLog("mobile session validation failed", {
          message: error?.message,
        });
              }
    };

    validateMobileSession();
  }, [appendMobileLog, canUseDirectEmbeddedConnect, logout, setSource, setAuthSource]);

  useEffect(() => {
    if (!canUseDirectEmbeddedConnect) return;
    if (isAuthenticated) return;

    const freshLoginInProgress =
      sessionStorage.getItem("v3tax:mobile_fresh_login") === "1";
    if (!freshLoginInProgress) return;
    if (!account) return;
    if (embeddedLoginInFlightRef.current) return;

    const manualDisconnect =
      sessionStorage.getItem(MOBILE_MANUAL_DISCONNECT_KEY) === "1";

    if (manualDisconnect) {
      appendMobileLog("embedded auto-login blocked because user manually disconnected");
      return;
    }

    const normalizedAccount = normalizeWalletAddress(account);
    if (!normalizedAccount) return;

    appendMobileLog("embedded account-effect finalizing login", {
      account: normalizedAccount,
      embeddedFlowActive: embeddedVeWorldFlowRef.current,
    });

    embeddedLoginInFlightRef.current = true;

    finishEmbeddedVeWorldLogin(normalizedAccount)
      .catch((error) => {
        appendMobileLog("embedded account-effect login failed", {
          message: error?.message,
        });
        sessionStorage.removeItem("v3tax:mobile_fresh_login");
        sessionStorage.removeItem("v3tax:force_logged_out");
        sessionStorage.removeItem("v3tax:expected_wallet_after_login");
        showMobileNotice(
          language === "en" ? "Login failed." : "Login fehlgeschlagen.",
          "error",
          3600
        );
      })
      .finally(() => {
        embeddedLoginInFlightRef.current = false;
        embeddedVeWorldFlowRef.current = false;
      });
  }, [
    account,
    appendMobileLog,
    canUseDirectEmbeddedConnect,
    finishEmbeddedVeWorldLogin,
    isAuthenticated,
    language,
    normalizeWalletAddress,
    showMobileNotice,
  ]);

  useEffect(() => {
    const onClick = (event) => {
      if (!event.target.closest(".mobile-header-menu")) {
        setShowMobileMenu(false);
      }
      if (!event.target.closest(".mobile-user-menu")) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const loadDatasetIndex = useCallback(async () => {
    if (!isAuthenticated || !user?.walletAddress) {
      setDatasetIndex([]);
      setSummaryState(null);
      setMobileScreen("list");
      setDetailDatasetId(null);
      setExchangeCoverage(null);
      setCalculationState(INITIAL_CALCULATION_STATE);
      setUiState("idle");
      return;
    }

    try {
      const statusRes = await fetch(apiUrl("/api/auth/status"), {
        credentials: "include",
      });
      const statusData = statusRes.ok ? await statusRes.json() : {};
      const backendWallet = statusData?.walletAddress?.toLowerCase?.() || null;
      const localWallet = user?.walletAddress?.toLowerCase?.() || null;

      if (!backendWallet || !localWallet || backendWallet !== localWallet) {
        
        setDatasetIndex([]);
        setSummaryState(null);
        setMobileScreen("list");
        setDetailDatasetId(null);
        setExchangeCoverage(null);
        setCalculationState(INITIAL_CALCULATION_STATE);
        setUiState("empty");
        return;
      }

      if (shouldForceMobileSmartScanEmpty(localWallet)) {
        console.warn(
          "[Mobile Smart Scan Test] Wallet-Sets werden für Test-Wallet ignoriert:",
          localWallet
        );

        setDatasetIndex([]);
        setSummaryState(null);
        setMobileScreen("list");
        setDetailDatasetId(null);
        setExchangeCoverage(null);
        setCalculationState(INITIAL_CALCULATION_STATE);
        setUiState("empty");
        return;
      }
    } catch (err) {
            setDatasetIndex([]);
      setSummaryState(null);
      setMobileScreen("list");
      setDetailDatasetId(null);
      setExchangeCoverage(null);
      setCalculationState(INITIAL_CALCULATION_STATE);
      setUiState("empty");
      return;
    }

    setUiState("loading");

    try {
      const datasetsRes = await fetch(apiUrl("/api/datasets"), {
        credentials: "include",
      });

      if (!datasetsRes.ok) {
        throw new Error(`Datasets fetch failed (${datasetsRes.status})`);
      }

      const datasetsPayload = await datasetsRes.json();
      const allDatasets = sortDatasetsForMobile(parseDatasetList(datasetsPayload));

      setDatasetIndex(allDatasets);

      if (!allDatasets.length) {
        setSummaryState(null);
        setUiState("empty");
        return;
      }

      setUiState("idle");
    } catch (error) {
            setDatasetIndex([]);
      setSummaryState(null);
      setUiState("error");
    }
  }, [isAuthenticated, user?.walletAddress]);

  useEffect(() => {
    loadDatasetIndex();
  }, [loadDatasetIndex]);

  const selectedDataset = useMemo(() => {
    if (!detailDatasetId || mobileScreen !== "detail") return null;
    return datasetIndex.find((d) => String(d.id) === String(detailDatasetId)) || null;
  }, [datasetIndex, detailDatasetId, mobileScreen]);

  const walletSetYears = useMemo(
    () => getMobileWalletSetYears(datasetIndex),
    [datasetIndex]
  );

  useEffect(() => {
    if (!walletSetYears.length) return;
    setSelectedWalletSetYear((prev) =>
      walletSetYears.includes(Number(prev)) ? Number(prev) : walletSetYears[0]
    );
  }, [walletSetYears]);

  const datasetsForSelectedYear = useMemo(
    () =>
      datasetIndex.filter(
        (dataset) => Number(getDatasetYear(dataset)) === Number(selectedWalletSetYear)
      ),
    [datasetIndex, selectedWalletSetYear]
  );

  const visibleDatasetIdsKey = useMemo(
    () => datasetsForSelectedYear.map((dataset) => String(dataset.id)).join("|"),
    [datasetsForSelectedYear]
  );

  const openDatasetDetail = useCallback((dataset) => {
    if (!dataset?.id) return;
    setDetailDatasetId(dataset.id);
    setMobileScreen("detail");
    setSummaryState(null);
    setUiState("idle");
    setExchangeCoverage(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const closeDatasetDetail = useCallback(() => {
    setMobileScreen("list");
    setDetailDatasetId(null);
    setSummaryState(null);
    setUiState("idle");
    setExchangeCoverage(null);
    setCalculationState(INITIAL_CALCULATION_STATE);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const loadSelectedSnapshot = useCallback(async () => {
    if (mobileScreen !== "detail") {
      setSummaryState(null);
      setIsRefreshing(false);
      return;
    }
    if (!isAuthenticated || !user?.walletAddress) {
      setSummaryState(null);
      setIsRefreshing(false);
      setUiState("idle");
      return;
    }
    if (!selectedDataset) {
      setSummaryState(null);
      setIsRefreshing(false);
      setUiState("idle");
      return;
    }

    const normalizedMeta = normalizeDatasetMeta(selectedDataset);

    if (!getSnapshotExists(selectedDataset)) {
      setSummaryState(null);
      setIsRefreshing(false);
      setUiState("empty");
      return;
    }

    const cacheKey = `${normalizedMeta.id}:${normalizedMeta.year}`;
    const cachedSummary = snapshotSummaryCacheRef.current.get(cacheKey);
    if (cachedSummary) {
      setSummaryState(cachedSummary);
      setIsRefreshing(false);
      setUiState("ready");
      return;
    }

    setSummaryState(null);
    setIsRefreshing(true);
    setUiState("loading");

    try {
      const snapshotRes = await fetch(
        apiUrl(
          `/api/datasets/${normalizedMeta.id}/snapshot-summary?year=${normalizedMeta.year}`
        ),
        { credentials: "include" }
      );

      if (!snapshotRes.ok) {
        setSummaryState(null);
        setIsRefreshing(false);
        setUiState("empty");
        return;
      }

      const snapshotPayload = await snapshotRes.json();
      const mapped = mapSummaryFromPayload({
        payload: snapshotPayload,
        datasetMeta: normalizedMeta,
        fallbackWallet: user.walletAddress,
      });

      if (!mapped) {
        setSummaryState(null);
        setIsRefreshing(false);
        setUiState("empty");
        return;
      }

      snapshotSummaryCacheRef.current.set(cacheKey, mapped);
      setSummaryState(mapped);
      setIsRefreshing(false);
      setUiState("ready");
    } catch (error) {
            setSummaryState(null);
      setIsRefreshing(false);
      setUiState("error");
    }
  }, [mobileScreen, isAuthenticated, user?.walletAddress, selectedDataset]);

  useEffect(() => {
    loadSelectedSnapshot();
  }, [loadSelectedSnapshot]);

  const pollAnalysisJob = useCallback(
    async (jobId, signal) => {
      while (!signal?.aborted) {
        const res = await fetch(apiUrl(`/api/analysis-jobs/${jobId}`), {
          credentials: "include",
          signal,
        });

        const data = await res.json();

        if (!res.ok || !data?.ok || !data?.job) {
          throw new Error(data?.error || "Failed to poll analysis job");
        }

        applyMobileAnalysisJobState(data.job);

        if (data.job.status === "done") {
          return data.job;
        }

        if (data.job.status === "error") {
          throw new Error(
            data.job?.error?.message || data.job?.detail || "Analysis job failed"
          );
        }

        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      throw new Error("Analysis polling aborted");
    },
    [applyMobileAnalysisJobState]
  );

  const fetchAnalysisJobResult = useCallback(async (jobId, signal) => {
    const res = await fetch(apiUrl(`/api/analysis-jobs/${jobId}/result`), {
      credentials: "include",
      signal,
    });

    const data = await res.json();

    if (!res.ok || !data?.ok) {
      throw new Error(data?.error || "Failed to load analysis result");
    }

    return data.result;
  }, []);

  const pollInstantScanJob = useCallback(
    async (jobId, signal) => {
      while (!signal?.aborted) {
        const res = await fetch(apiUrl(`/api/analysis-jobs/${jobId}`), {
          credentials: "include",
          signal,
        });

        const data = await res.json();

        if (!res.ok || !data?.ok || !data?.job) {
          throw new Error(data?.error || "Failed to poll instant scan job");
        }

        applyInstantScanJobState(data.job);

        if (data.job.status === "done") {
          return data.job;
        }

        if (data.job.status === "error") {
          throw new Error(
            data.job?.error?.message || data.job?.detail || "Instant scan failed"
          );
        }

        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      throw new Error("Instant scan polling aborted");
    },
    [applyInstantScanJobState]
  );

  const startMobileInstantScan = useCallback(
    async ({ forceRefresh = false, targetWallet = "" } = {}) => {
      if (instantScanStartedRef.current) return;

      const wallet = String(user?.walletAddress || "").toLowerCase().trim();
      if (!wallet) return;

      const normalizedTargetWallet = normalizeDatasetAddress(targetWallet);
      const scanWallet = normalizedTargetWallet || wallet;
      const isAdminTargetScan = Boolean(
        normalizedTargetWallet && normalizedTargetWallet !== wallet
      );

      const scanYears = getMobileInstantScanYears();
      if (!scanYears.length) return;

      if (!forceRefresh) {
        setInstantScanState((prev) =>
          prev.status === "idle"
            ? {
                ...INITIAL_INSTANT_SCAN_STATE,
                status: "loading",
                message:
                  language === "en"
                    ? "Loading Smart Scan snapshot"
                    : "Smart-Scan-Snapshot wird geladen",
              }
            : prev
        );

        try {
          const snapshotQuery = isAdminTargetScan
            ? `?scanWallet=${encodeURIComponent(scanWallet)}`
            : "";
          const snapshotRes = await fetch(
            apiUrl(`/api/mobile/instant-scan/snapshot${snapshotQuery}`),
            { credentials: "include" }
          );
          const snapshotData = await snapshotRes.json().catch(() => null);
          if (
            snapshotRes.ok &&
            snapshotData?.ok &&
            snapshotData?.exists &&
            snapshotData?.summary
          ) {
            setInstantScanState({
              ...INITIAL_INSTANT_SCAN_STATE,
              status: "ready",
              progress: 100,
              currentStep: "done",
              result: snapshotData.summary,
              fromSnapshot: true,
              updatedAt: snapshotData.updatedAt,
              refreshedAt: snapshotData.refreshedAt,
            });
            void trackPlatformActivity("mobile_smart_scan_ready", {
              source: "mobile",
              walletAddress: scanWallet,
              fromSnapshot: true,
              forceRefresh: false,
              years: snapshotData.summary?.years || null,
              totalRelevantTransactions: Number(
                snapshotData.summary?.totalRelevantTransactions || 0
              ),
            });
            return;
          }
        } catch (error) {
          console.warn("[Mobile Smart Scan] Snapshot load failed:", error);
        }
      }

      instantScanStartedRef.current = true;

      const yearsLabel = scanYears.join(" · ");

      setInstantScanState({
        ...INITIAL_INSTANT_SCAN_STATE,
        status: "running",
        currentStep: "initializing",
        progress: 5,
        message: getMobileStepLabel("initializing", language),
        detail:
          language === "en"
            ? `${isAdminTargetScan ? "Scanning admin test wallet" : "Scanning connected wallet"} · ${yearsLabel}`
            : `${isAdminTargetScan ? "Admin-Test-Wallet wird gescannt" : "Verbundene Wallet wird gescannt"} · ${yearsLabel}`,
      });

      const controller = new AbortController();

      try {
        const startRes = await fetch(
          apiUrl("/api/mobile/instant-scan/start"),
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              forceRefresh,
              scanWallet: isAdminTargetScan ? scanWallet : undefined,
            }),
            signal: controller.signal,
          }
        );

        const startData = await startRes.json().catch(() => null);

        if (startData?.fromSnapshot && startData?.summary) {
          instantScanStartedRef.current = false;
          setInstantScanState({
            ...INITIAL_INSTANT_SCAN_STATE,
            status: "ready",
            progress: 100,
            currentStep: "done",
            result: startData.summary,
            fromSnapshot: true,
            updatedAt: startData.updatedAt,
            refreshedAt: startData.refreshedAt,
          });
          void trackPlatformActivity("mobile_smart_scan_ready", {
            source: "mobile",
            walletAddress: scanWallet,
            fromSnapshot: true,
            forceRefresh,
            years: startData.summary?.years || null,
            totalRelevantTransactions: Number(
              startData.summary?.totalRelevantTransactions || 0
            ),
          });
          return;
        }

        if (!startRes.ok || !startData?.ok || !startData?.jobId) {
          throw new Error(
            startData?.error ||
              `Instant scan start failed (${startRes.status})`
          );
        }

        if (startData.job) {
          applyInstantScanJobState(startData.job);
        }

        await pollInstantScanJob(startData.jobId, controller.signal);
        const jobResult = await fetchAnalysisJobResult(
          startData.jobId,
          controller.signal
        );

        const summary = jobResult?.summary || null;

        setInstantScanState({
          ...INITIAL_INSTANT_SCAN_STATE,
          status: "ready",
          progress: 100,
          currentStep: "done",
          result: summary,
        });
        void trackPlatformActivity("mobile_smart_scan_ready", {
          source: "mobile",
          walletAddress: scanWallet,
          fromSnapshot: false,
          forceRefresh,
          years: summary?.years || null,
          totalRelevantTransactions: Number(
            summary?.totalRelevantTransactions || 0
          ),
        });
        instantScanStartedRef.current = false;
      } catch (error) {
        instantScanStartedRef.current = false;
        setInstantScanState({
          ...INITIAL_INSTANT_SCAN_STATE,
          status: "error",
          error: { message: error?.message || "Unknown error" },
        });
      }
    },
    [
      applyInstantScanJobState,
      fetchAnalysisJobResult,
      language,
      pollInstantScanJob,
      user?.walletAddress,
    ]
  );

  // Smart Scan wird für jede eingeloggte Wallet geladen – unabhängig davon,
  // ob bereits Wallet-Sets existieren. Zuerst wird der gespeicherte Snapshot
  // geladen; wenn keiner existiert, startet ein einmaliger Connected-Wallet Scan.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (authLoading) return;
    if (!user?.walletAddress) return;
    if (uiState === "loading") return;
    if (instantScanStartedRef.current) return;
    if (instantScanState.status !== "idle") return;

    startMobileInstantScan();
  }, [
    isAuthenticated,
    authLoading,
    user?.walletAddress,
    uiState,
    instantScanState.status,
    startMobileInstantScan,
  ]);

  const loadExchangeCoverage = useCallback(async () => {
    if (mobileScreen !== "detail" || !selectedDataset?.id) {
      setExchangeCoverage(null);
      return;
    }

    try {
      const res = await fetch(
        apiUrl(`/api/datasets/${selectedDataset.id}/exchange-imports`),
        { credentials: "include" }
      );

      if (!res.ok) {
        setExchangeCoverage({
          rowCount: 0,
          sourceCount: 0,
          latestImport: null,
          hasCsv: false,
          mode: "wallet-only",
        });
        return;
      }

      const payload = await res.json();
      setExchangeCoverage(buildExchangeCoverage(payload));
    } catch (error) {
            setExchangeCoverage({
        rowCount: 0,
        sourceCount: 0,
        latestImport: null,
        hasCsv: false,
        mode: "wallet-only",
      });
    }
  }, [mobileScreen, selectedDataset?.id]);

  useEffect(() => {
    loadExchangeCoverage();
  }, [loadExchangeCoverage]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (mobileScreen !== "list") return;
    if (!datasetsForSelectedYear.length) return;

    let cancelled = false;

    const loadVisibleCoverage = async () => {
      const entries = await Promise.all(
        datasetsForSelectedYear.map(async (dataset) => {
          const datasetId = dataset?.id;
          if (!datasetId) return null;
          try {
            const res = await fetch(
              apiUrl(`/api/datasets/${datasetId}/exchange-imports`),
              { credentials: "include" }
            );
            if (!res.ok) {
              return [
                String(datasetId),
                {
                  rowCount: 0,
                  sourceCount: 0,
                  latestImport: null,
                  hasCsv: false,
                  mode: "wallet-only",
                },
              ];
            }
            const payload = await res.json();
            return [String(datasetId), buildExchangeCoverage(payload)];
          } catch {
            return [
              String(datasetId),
              {
                rowCount: 0,
                sourceCount: 0,
                latestImport: null,
                hasCsv: false,
                mode: "wallet-only",
              },
            ];
          }
        })
      );

      if (cancelled) return;
      const next = Object.fromEntries(entries.filter(Boolean));
      setExchangeCoverageByDataset((prev) => ({
        ...prev,
        ...next,
      }));
    };

    loadVisibleCoverage();

    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    mobileScreen,
    selectedWalletSetYear,
    visibleDatasetIdsKey,
    datasetsForSelectedYear,
  ]);

  const handleStartDatasetCalculation = useCallback(
    async (datasetOverride = null) => {
      const targetDataset =
        datasetOverride && datasetOverride.id ? datasetOverride : selectedDataset;
      if (!targetDataset?.id || calculationState.isRunning) return;

      const invMeta = normalizeDatasetMeta(targetDataset);
      if (invMeta?.id != null && invMeta?.year != null) {
        snapshotSummaryCacheRef.current.delete(`${invMeta.id}:${invMeta.year}`);
      }

      if (String(detailDatasetId || "") !== String(targetDataset.id)) {
        setDetailDatasetId(targetDataset.id);
        setMobileScreen("detail");
        setSummaryState(null);
        setUiState("idle");
        setExchangeCoverage(null);
      }

      const controller = new AbortController();

      try {
        setCalculationState({
          isRunning: true,
          message: language === "en" ? "Preparing analysis" : "Analyse vorbereiten",
          detail: `${language === "en" ? "Tax year" : "Steuerjahr"} ${targetDataset.year}`,
          jobId: null,
          progress: 5,
          currentStep: "initializing",
          completedSteps: [],
          indeterminate: false,
        });

        const startRes = await fetch(
          apiUrl(`/api/datasets/${targetDataset.id}/calculate/start`),
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              year: Number(targetDataset.year),
            }),
            signal: controller.signal,
          }
        );

        const startPayload = await startRes.json().catch(() => null);

        if (!startRes.ok) {
          if (startRes.status === 409) {
            showMobileNotice(
              language === "en"
                ? "A calculation is already running for this dataset."
                : "Für dieses Dataset läuft bereits eine Berechnung.",
              "warning",
              3600
            );
            return;
          }
          throw new Error(
            startPayload?.error || `Calculation start failed (${startRes.status})`
          );
        }

        const jobId = startPayload?.jobId || startPayload?.job?.id;
        if (!jobId) {
          throw new Error("No jobId returned from backend");
        }

        if (startPayload?.job) {
          applyMobileAnalysisJobState(startPayload.job);
        } else {
          setCalculationState((prev) => ({
            ...prev,
            isRunning: true,
            jobId,
          }));
        }

        await pollAnalysisJob(jobId, controller.signal);
        const analysisResult = await fetchAnalysisJobResult(jobId, controller.signal);

        const mapped = mapSummaryFromPayload({
          payload: analysisResult,
          datasetMeta: normalizeDatasetMeta(targetDataset),
          fallbackWallet: user?.walletAddress,
        });

        const metaForCache = normalizeDatasetMeta(targetDataset);
        if (mapped) {
          const withTs = {
            ...mapped,
            lastCalculatedAt: new Date().toISOString(),
          };
          if (metaForCache?.id != null && metaForCache?.year != null) {
            snapshotSummaryCacheRef.current.set(
              `${metaForCache.id}:${metaForCache.year}`,
              withTs
            );
          }
          setSummaryState(withTs);
          setUiState("ready");
        }

        await loadDatasetIndex();
        await loadExchangeCoverage();

        showMobileNotice(
          language === "en" ? "Dataset updated." : "Dataset aktualisiert.",
          "success",
          2200
        );
      } catch (error) {
        showMobileNotice(
          language === "en" ? "Calculation failed." : "Berechnung fehlgeschlagen.",
          "error",
          3600
        );
      } finally {
        setCalculationState(INITIAL_CALCULATION_STATE);
        setIsRefreshing(false);
      }
    },
    [
      selectedDataset,
      detailDatasetId,
      calculationState.isRunning,
      language,
      applyMobileAnalysisJobState,
      pollAnalysisJob,
      fetchAnalysisJobResult,
      user?.walletAddress,
      loadDatasetIndex,
      loadExchangeCoverage,
      showMobileNotice,
    ]
  );

  const handleMobileLogout = useCallback(async () => {
    appendMobileLog("handleMobileLogout started");

    closeAllMenus();

    sessionStorage.setItem(MOBILE_MANUAL_DISCONNECT_KEY, "1");
    sessionStorage.setItem("v3tax:force_logged_out", "1");
    sessionStorage.removeItem("v3tax:mobile_fresh_login");

    localStorage.removeItem("v3tax_auth");

    resetMobileWalletUiState();

    try {
      await logout();
      appendMobileLog("backend logout done");
    } catch (error) {
      appendMobileLog("backend logout failed", {
        message: error?.message,
      });
    }

    try {
      disconnectWalletKit?.();
      appendMobileLog("dapp-kit wallet.disconnect after mobile logout");
    } catch (error) {
      appendMobileLog("dapp-kit wallet.disconnect after mobile logout failed", {
        message: error?.message,
      });
    }

    try {
      setSource(null);
      setAuthSource(null);
      appendMobileLog("mobile logout reset sources done");
    } catch (error) {
      appendMobileLog("mobile logout reset sources failed", {
        message: error?.message,
      });
    }

    try {
      await withTimeout(checkAuthStatus({ silent: true }), 2500, "post logout auth check");
    } catch (error) {
      appendMobileLog("post logout auth check ignored", {
        message: error?.message,
      });
    }

    showMobileNotice(
      language === "en" ? "Wallet disconnected." : "Wallet getrennt.",
      "success",
      1800
    );
  }, [
    appendMobileLog,
    checkAuthStatus,
    closeAllMenus,
    disconnectWalletKit,
    language,
    logout,
    resetMobileWalletUiState,
    setAuthSource,
    setSource,
    showMobileNotice,
  ]);

  const handleSwitchAccount = useCallback(async () => {
    try {
      appendMobileLog("handleSwitchAccount -> hard fresh mobile login");
      closeAllMenus();

      sessionStorage.removeItem(MOBILE_MANUAL_DISCONNECT_KEY);

      resetMobileWalletUiState();

      await startFreshMobileLogin({ forceFresh: true });
    } catch (error) {
      appendMobileLog("handleSwitchAccount failed", {
        message: error?.message,
        stack: error?.stack,
      });

      showMobileNotice(
        language === "en"
          ? "Account switch failed."
          : "Account-Wechsel fehlgeschlagen.",
        "error",
        3600
      );
    }
  }, [
    appendMobileLog,
    closeAllMenus,
    language,
    resetMobileWalletUiState,
    startFreshMobileLogin,
    showMobileNotice,
  ]);

  const shortWallet = user?.walletAddress
    ? `${user.walletAddress.slice(0, 6)}...${user.walletAddress.slice(-4)}`
    : "";

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <div className="pointer-events-none fixed inset-x-0 top-0 z-[110] h-[22px] bg-blue-50" />

        {/* Header */}
        <div
          className="fixed top-3 left-1/2 z-[120] isolate flex h-[64px] w-full max-w-[1600px] -translate-x-1/2 items-center justify-between rounded-[24px] border border-white/12 bg-[linear-gradient(135deg,#2563eb_0%,#2458dc_55%,#1e40af_100%)] px-3 shadow-[0_16px_40px_rgba(15,23,42,0.20)]"
          style={{ width: "calc(100% - 24px)", maxWidth: "1600px" }}
        >
          <div className="mobile-header-menu relative flex items-center gap-2.5">
            <button
              onClick={() => setShowMobileMenu((prev) => !prev)}
              className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/16 focus:outline-none"
              aria-label={language === "en" ? "Toggle navigation" : "Navigation öffnen"}
            >
              {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>

            <div className="flex flex-col leading-none">
              <span className="text-[15px] font-semibold tracking-tight text-white">V3Tax</span>
              <span className="mt-1 text-[11px] text-blue-100/95">VeChain • {APP_VERSION_LABEL}</span>
            </div>

            {showMobileMenu && (
              <div className="absolute left-0 top-[calc(100%+10px)] z-[130] w-[284px] overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.22)]">
                <div className="border-b border-slate-100 bg-[linear-gradient(180deg,#eff6ff_0%,#ffffff_100%)] px-4 py-3">
                  <div className="text-xs font-semibold text-slate-700">
                    {language === "en" ? "Navigation" : "Navigation"}
                  </div>
                </div>

                <div className="p-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowFunctionModal(true);
                      closeAllMenus();
                    }}
                    className="flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left text-sm text-slate-700 transition hover:bg-blue-50/80"
                  >
                    <div>
                      <div className="font-semibold">
                        {language === "en" ? "How V3Tax Works" : "Wie V3Tax funktioniert"}
                      </div>
                      <div className="text-xs text-slate-500">
                        {language === "en" ? "Functions & features" : "Funktionen & Features"}
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setShowRoadmap(true);
                      closeAllMenus();
                    }}
                    className="flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left text-sm text-slate-700 transition hover:bg-blue-50/80"
                  >
                    <div>
                      <div className="font-semibold">
                        {language === "en" ? "Roadmap" : "Roadmap"}
                      </div>
                      <div className="text-xs text-slate-500">
                        {language === "en" ? "Planned features & limits" : "Geplante Features & Limits"}
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setShowLegalModal(true);
                      closeAllMenus();
                    }}
                    className="flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left text-sm text-slate-700 transition hover:bg-blue-50/80"
                  >
                    <div>
                      <div className="font-semibold">
                        {language === "en" ? "Legal Notices" : "Rechtliche Hinweise"}
                      </div>
                      <div className="text-xs text-slate-500">
                        {language === "en" ? "Terms & privacy" : "Rechtliches & Datenschutz"}
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setShowHelpModal(true);
                      closeAllMenus();
                    }}
                    className="flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left text-sm text-slate-700 transition hover:bg-blue-50/80"
                  >
                    <div>
                      <div className="font-semibold">
                        {language === "en" ? "Help & Support" : "Hilfe & Support"}
                      </div>
                      <div className="text-xs text-slate-500">
                        {language === "en" ? "Get help & documentation" : "Hilfe & Dokumentation"}
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {isAuthenticated && user ? (
              <div className="mobile-user-menu relative">
                <button
                  onClick={() => setShowUserMenu((prev) => !prev)}
                  className="flex h-11 min-w-0 max-w-[148px] items-center gap-2 rounded-[18px] border border-white/12 bg-white/10 px-3 text-white shadow-sm backdrop-blur-sm transition hover:bg-white/14"
                >
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-emerald-400 shadow-[0_0_0_4px_rgba(74,222,128,0.12)]" />
                  <span className="min-w-0 truncate text-sm font-semibold tracking-tight">{shortWallet}</span>
                  <ChevronDown
                    className={`h-3.5 w-3.5 shrink-0 transition-transform ${showUserMenu ? "rotate-180" : ""}`}
                  />
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 top-[calc(100%+10px)] z-[130] w-[236px] overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.22)]">
                    <div className="border-b border-slate-100 bg-[linear-gradient(180deg,#f8fafc_0%,#ffffff_100%)] px-4 py-3">
                      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                        {language === "en" ? "Connection" : "Verbindung"}
                      </div>
                      <div className="mt-2 break-all text-sm font-semibold text-slate-900">
                        {user.walletAddress}
                      </div>
                    </div>

                    <div className="p-2">
                      <button
                        type="button"
                        onClick={handleSwitchAccount}
                        className="flex w-full items-center justify-between rounded-xl px-4 py-3 text-left text-sm text-slate-700 transition hover:bg-blue-50"
                      >
                        <div>
                          <div className="font-semibold">
                            {language === "en" ? "Connect another wallet" : "Andere Wallet verbinden"}
                          </div>
                          <div className="text-xs text-slate-500">
                            {isEmbeddedVeWorld
                              ? language === "en"
                                ? "Reconnect inside VeWorld"
                                : "Innerhalb von VeWorld neu verbinden"
                              : language === "en"
                                ? "Start fresh VeWorld login"
                                : "Neue VeWorld-Anmeldung starten"}
                          </div>
                        </div>
                        <ChevronDown className="h-4 w-4 -rotate-90 text-slate-400" />
                      </button>

                      <button
                        type="button"
                        onClick={handleMobileLogout}
                        className="mt-1 flex w-full items-center justify-between rounded-xl px-4 py-3 text-left text-sm text-red-700 transition hover:bg-red-50"
                      >
                        <div>
                          <div className="font-semibold">
                            {language === "en" ? "Disconnect wallet" : "Wallet trennen"}
                          </div>
                          <div className="text-xs text-red-500/80">
                            {language === "en"
                              ? "Return to the start screen"
                              : "Zurück zum Startbildschirm"}
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            <button
              type="button"
              onClick={() => changeLanguage(language === "de" ? "en" : "de")}
              className="inline-flex h-11 min-w-[54px] items-center justify-center rounded-[18px] border border-white/12 bg-white/10 px-3 text-sm font-semibold text-white transition hover:bg-white/14"
              aria-label={language === "en" ? "Switch language" : "Sprache wechseln"}
            >
              {language?.toUpperCase?.() || "EN"}
            </button>
          </div>
        </div>

        {/* Content */}
        <div
          className="mx-auto w-full max-w-[1600px] px-0 pb-10 pt-[92px]"
          style={{ width: "calc(100% - 24px)", maxWidth: "1600px" }}
        >
          {!isAuthenticated ? (
            <MobileStatePanel
              language={language}
              eyebrow="MOBILE COMPANION"
              title="V3Tax Mobile"
              description={
                language === "en"
                  ? "Connect your VeWorld wallet to view tax snapshots and Smart Scan activity."
                  : "Verbinde deine VeWorld Wallet, um Steuer-Snapshots und Smart-Scan-Aktivität mobil anzusehen."
              }
              note={
                language === "en"
                  ? "Wallet Set setup and purchases are completed on a PC, Mac, or laptop. Mobile is optimized for snapshots, refreshes and quick previews."
                  : "Wallet-Set-Einrichtung und Käufe erfolgen auf einem PC oder Laptop. Mobile ist für Snapshots, Aktualisierungen und schnelle Vorschauen optimiert."
              }
              ctaLabel={
                isEmbeddedVeWorld
                  ? language === "en"
                    ? "Connect wallet"
                    : "Wallet verbinden"
                  : language === "en"
                    ? "Sign in with VeWorld"
                    : "Mit VeWorld anmelden"
              }
              ctaIcon={Link2}
              onCta={startFreshMobileLogin}
            />
          ) : authLoading &&
            (typeof sessionStorage === "undefined" ||
              sessionStorage.getItem("v3tax:mobile_fresh_login") !== "1") ? (
            <div className="rounded-[28px] border border-slate-200 bg-white/95 p-8 text-center shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
              <p className="mt-4 text-sm text-slate-600">
                {language === "en"
                  ? "Loading your mobile dashboard..."
                  : "Mobiles Dashboard wird geladen..."}
              </p>
            </div>
          ) : typeof sessionStorage !== "undefined" &&
            sessionStorage.getItem("v3tax:mobile_fresh_login") === "1" ? (
            <div className="rounded-[28px] border border-blue-100 bg-white/95 p-8 text-center shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
              <p className="mt-4 text-sm font-semibold text-slate-800">
                {language === "en"
                  ? "Completing VeWorld login..."
                  : "VeWorld Login wird abgeschlossen..."}
              </p>
              <p className="mx-auto mt-2 max-w-[260px] text-xs leading-5 text-slate-500">
                {language === "en"
                  ? "If you already signed, this should only take a moment."
                  : "Wenn du bereits signiert hast, dauert es nur noch einen Moment."}
              </p>
            </div>
          ) : uiState === "loading" && datasetIndex.length === 0 ? (
            <div className="rounded-[28px] border border-slate-200 bg-white/95 p-8 text-center shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
              <p className="mt-4 text-sm text-slate-600">
                {language === "en"
                  ? "Loading your mobile dashboard..."
                  : "Mobiles Dashboard wird geladen..."}
              </p>
            </div>
          ) : uiState === "error" && datasetIndex.length === 0 ? (
            <div className="rounded-[28px] border border-rose-200 bg-white/95 p-6 shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
              <h2 className="text-lg font-semibold text-slate-900">
                {language === "en"
                  ? "Dashboard could not be loaded"
                  : "Dashboard konnte nicht geladen werden"}
              </h2>

              <p className="mt-2 text-sm leading-6 text-slate-600">
                {language === "en"
                  ? "Please try again. If the issue continues, reconnect your wallet."
                  : "Bitte versuche es erneut. Falls das Problem bestehen bleibt, verbinde deine Wallet erneut."}
              </p>

              <div className="mt-5">
                <button
                  type="button"
                  onClick={() => loadDatasetIndex()}
                  className="inline-flex w-full items-center justify-center rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-3 font-semibold text-white"
                >
                  {language === "en" ? "Try again" : "Erneut versuchen"}
                </button>
              </div>
            </div>
          ) : !datasetIndex.length ? (
            <div className="space-y-5">
              <MobileSmartScanPanel
                language={language}
                instantScanState={instantScanState}
                startMobileInstantScan={startMobileInstantScan}
                instantScanStartedRef={instantScanStartedRef}
                walletAddress={effectiveMobileInstantScanWallet}
                isAdminInstantScan={isMobileInstantScanAdmin}
                adminScanWalletInput={adminInstantScanWalletInput}
                onAdminScanWalletInputChange={setAdminInstantScanWalletInput}
                desktopLinkCopied={desktopLinkCopied}
                desktopLinkSharing={desktopLinkSharing}
                onCopyDesktopLink={handleCopyDesktopLink}
                onShareDesktopLink={handleShareDesktopLink}
                onDesktopHandoffView={handleDesktopHandoffView}
              />
              <MobileWalletSetDashboardPanel
                language={language}
                datasetIndex={datasetIndex}
                walletSetYears={walletSetYears}
                selectedWalletSetYear={selectedWalletSetYear}
                setSelectedWalletSetYear={setSelectedWalletSetYear}
                datasetsForSelectedYear={datasetsForSelectedYear}
                exchangeCoverageByDataset={exchangeCoverageByDataset}
                openDatasetDetail={openDatasetDetail}
                desktopLinkCopied={desktopLinkCopied}
                desktopLinkSharing={desktopLinkSharing}
                onCopyDesktopLink={handleCopyDesktopLink}
                onShareDesktopLink={handleShareDesktopLink}
                onDesktopHandoffView={handleDesktopHandoffView}
              />
            </div>
          ) : mobileScreen === "list" ? (
            <div className="space-y-5">
              <MobileSmartScanPanel
                language={language}
                instantScanState={instantScanState}
                startMobileInstantScan={startMobileInstantScan}
                instantScanStartedRef={instantScanStartedRef}
                walletAddress={effectiveMobileInstantScanWallet}
                isAdminInstantScan={isMobileInstantScanAdmin}
                adminScanWalletInput={adminInstantScanWalletInput}
                onAdminScanWalletInputChange={setAdminInstantScanWalletInput}
                desktopLinkCopied={desktopLinkCopied}
                desktopLinkSharing={desktopLinkSharing}
                onCopyDesktopLink={handleCopyDesktopLink}
                onShareDesktopLink={handleShareDesktopLink}
                onDesktopHandoffView={handleDesktopHandoffView}
              />
              <MobileWalletSetDashboardPanel
                language={language}
                datasetIndex={datasetIndex}
                walletSetYears={walletSetYears}
                selectedWalletSetYear={selectedWalletSetYear}
                setSelectedWalletSetYear={setSelectedWalletSetYear}
                datasetsForSelectedYear={datasetsForSelectedYear}
                exchangeCoverageByDataset={exchangeCoverageByDataset}
                openDatasetDetail={openDatasetDetail}
                desktopLinkCopied={desktopLinkCopied}
                desktopLinkSharing={desktopLinkSharing}
                onCopyDesktopLink={handleCopyDesktopLink}
                onShareDesktopLink={handleShareDesktopLink}
                onDesktopHandoffView={handleDesktopHandoffView}
              />
            </div>
          ) : selectedDataset ? (
            <div className="space-y-4 pb-10">
              <MobileWalletSetHero
                dataset={selectedDataset}
                language={language}
                onBack={closeDatasetDetail}
                calculationState={calculationState}
                onStartCalculation={() => handleStartDatasetCalculation()}
              />

              {calculationState.isRunning ? (
                <DatasetCalculationStatus
                  language={language}
                  hasCsv={Boolean(exchangeCoverage?.hasCsv)}
                  progress={calculationState.progress}
                  currentStep={calculationState.currentStep}
                  completedSteps={calculationState.completedSteps}
                  message={calculationState.message}
                  detail={calculationState.detail}
                  indeterminate={calculationState.indeterminate}
                />
              ) : null}

              {selectedDataset && uiState === "empty" ? (
                <section className="rounded-[28px] border border-amber-200 bg-amber-50/70 p-5 shadow-sm">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700">
                    {language === "en" ? "No calculation available" : "Keine Berechnung verfügbar"}
                  </div>
                  <h3 className="mt-2 text-xl font-semibold tracking-tight text-slate-950">
                    {language === "en"
                      ? "No calculation is available yet"
                      : "Noch keine Berechnung verfügbar"}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {language === "en"
                      ? "Start the analysis to create the first mobile result view for this wallet set."
                      : "Starte die Analyse, um die erste mobile Ergebnisansicht für dieses Wallet-Set zu erstellen."}
                  </p>
                </section>
              ) : null}

              {isRefreshing || uiState === "loading" ? (
                <div className="rounded-[28px] border border-slate-200 bg-white/95 p-8 text-center shadow-[0_14px_38px_rgba(15,23,42,0.06)]">
                  <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
                  <p className="mt-4 text-sm text-slate-600">
                    {language === "en"
                      ? "Loading calculation results…"
                      : "Berechnungsergebnisse werden geladen…"}
                  </p>
                </div>
              ) : null}

              {uiState === "error" ? (
                <div className="rounded-[28px] border border-rose-200 bg-white/95 p-5 shadow-[0_14px_38px_rgba(15,23,42,0.06)]">
                  <h2 className="text-lg font-semibold text-slate-900">
                    {language === "en"
                      ? "Results could not be loaded"
                      : "Ergebnisse konnten nicht geladen werden"}
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    {language === "en"
                      ? "Please try again. If the issue continues, reconnect your wallet."
                      : "Bitte versuche es erneut. Falls das Problem bestehen bleibt, verbinde deine Wallet erneut."}
                  </p>
                </div>
              ) : null}

              {calculationState.isRunning ? (
                <DashboardCalculationPlaceholder language={language} />
              ) : uiState === "ready" && summaryState?.dashboardProps ? (
                <div className="-mt-1">
                  <Dashboard
                    {...summaryState.dashboardProps}
                    embedded
                    showHeader={false}
                    compact={false}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <MobileFooter
          language={language}
          onOpenLegal={() => setShowLegalModal(true)}
          pushBelowFold={
            !isAuthenticated ||
            (isAuthenticated &&
              !authLoading &&
              !isRefreshing &&
              uiState !== "loading" &&
              uiState !== "error" &&
              datasetIndex.length === 0)
          }
        />
      </div>

      <WalletConnectModal
        isOpen={showWalletModal && !canUseDirectEmbeddedConnect}
        onClose={() => {
          setShowWalletModal(false);
          setForceFreshVeWorldLogin(false);
          sessionStorage.removeItem("v3tax:mobile_fresh_login");
        }}
        resetQrCode={resetQrCode}
        forceFreshVeWorldLogin={forceFreshVeWorldLogin}
        onOpenLegal={() => setShowLegalModal(true)}
        onSuccess={async (_account, walletType) => {
          sessionStorage.removeItem("v3tax:mobile_fresh_login");
          setForceFreshVeWorldLogin(false);

          try {
            const resolvedWalletType =
              walletType === "veworld" ? "veworld" : "wallet-connect";

            setSource(resolvedWalletType);
            setAuthSource(resolvedWalletType);

            if (resolvedWalletType === "veworld") {
              const walletAddress = normalizeWalletAddress(_account);

              if (!walletAddress) {
                throw new Error("Wallet address missing after successful connect");
              }

              const loginResult = await login({
                walletAddress,
                walletType: "veworld",
                source: "veworld",
              });

              if (!loginResult?.success) {
                throw new Error(loginResult?.error || "Backend login failed");
              }
            }

            await checkAuthStatus({ silent: true });

            setShowWalletModal(false);
            closeAllMenus();
          } catch (error) {
            showMobileNotice(
              language === "en"
                ? "Login failed."
                : "Login fehlgeschlagen.",
              "error",
              3600
            );
          }
        }}
        allowedWallets={["veworld"]}
        autoSelectWallet={null}
        uiVariant="desktop"
      />

      <HowV3TaxWorksModal
        open={showFunctionModal}
        onClose={() => setShowFunctionModal(false)}
        language={language}
      />

      <RoadmapModal
        open={showRoadmap}
        onClose={() => setShowRoadmap(false)}
        language={language}
      />

      <LegalNoticesModal
        open={showLegalModal}
        onClose={() => setShowLegalModal(false)}
        language={language}
      />

      <HelpSupportModal
        open={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        language={language}
      />

      <MobileNotice
        notice={mobileNotice}
        onDismiss={dismissMobileNotice}
      />
    </>
  );
}
